import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, lazy } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { nextTemplateWindowTime } from './templateWindowHelpers';

// Module-level lazy reference — MUST be outside the component to avoid recreating on every render.
// Putting React.lazy() inside a component body causes Suspense to never settle (it sees a new
// lazy reference each render and immediately suspends again).
//
// The .catch() here surfaces chunk-load failures (404, CSP block, network error) as a visible
// error panel instead of leaving the Suspense fallback frozen on "Loading 3D viewer..." forever.
const SkeletonViewer3D = lazy(() =>
  import('@/components/3d/SkeletonViewer3D').catch((err: unknown) => {
    console.error('[EnhancedEvaluationDisplay] 3D viewer chunk failed to load:', err);
    return {
      default: () => (
        <div className="aspect-video flex items-center justify-center bg-dark-base rounded-lg">
          <div className="text-center space-y-2">
            <p className="text-sm text-danger font-medium">3D viewer failed to load</p>
            <p className="text-xs text-text-muted">Open DevTools console for details</p>
          </div>
        </div>
      ),
    };
  })
);

// Eager prefetch of the 3D viewer chunk so the first 2D→3D toggle is instant.
// Called once at module import; the dynamic import returns a cached Promise.
const _prefetchSkeletonViewer3D = () => { void import('@/components/3d/SkeletonViewer3D'); };
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import {
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Button, Select, Card } from '@/components/ui';
import { BodyRegionOverlay } from '@/components/evaluation';
import { useVideoPlayer, useBodyRegionOverlay, usePhaseSync } from '@/hooks';
import { useVisualizationMode } from '@/hooks/useVisualizationMode';
import { useStableMediaSrc } from '@/hooks/useStableMediaSrc';
import useMultiTrajectoryTrails, {
  TRAIL_LANDMARK_PALETTE,
  MAX_TRAILS,
} from '@/hooks/useMultiTrajectoryTrails';
import { formatDuration } from '@/utils/formatters';
import { ENABLE_3D_VISUALIZATION, FIELD_IDS } from '@/utils/constants';
import {
  functionalChipLabel,
  pickLeadSideDefaults,
  type LeadSide,
} from '@/utils/leadSideLabels';
import { PhaseSyncTimeline } from '@/components/phase/PhaseSyncTimeline';
import UnifiedPoseOverlay from '@/components/canvas/UnifiedPoseOverlay';
import PoseOverlay from '@/components/canvas/PoseOverlay';
import { extractLandmarksFromFrame } from '@/hooks/usePoseExtraction';
import { mirrorLandmarks } from '@/utils/poseUtils';
import GoalOverlayLayer from '@/components/canvas/GoalOverlayLayer';
import TrajectoryTrailOverlay from '@/components/canvas/TrajectoryTrailOverlay';
import { planAthleteDrive, clampPlaybackRate } from '@/utils/phaseSyncDrive';
import EvalOverlayControlBar from './EvalOverlayControlBar';
import {
  type EvalOverlayState,
  DEFAULT_OVERLAY_STATE,
  overlayBitsToVizMode,
  togglePoseBit,
  toggleGhostBit,
} from './evalOverlayState';
import EvalViewModeSwitch from './EvalViewModeSwitch';
import type { PhaseMapping, SpatialGoalResult, AxisGoalResult, TimingGoalResult } from '@/types/api';
import type { LandmarkDeviationWithCoords } from '@/types/visualization';

interface FrameScore {
  frame_index: number;
  score: number;
  is_error: boolean;
  procrustes_disparity?: number;
}

interface RegionHull {
  region_id: string;
  region_label: string;
  region_score: number;
  hulls: Record<string, number[][]>;
}

/** A single pose frame as returned by the pose-extraction API — the shape
 *  actually read by meanVisibilityByLandmark / extractLandmarksFromFrame /
 *  GoalOverlayLayer (all of which accept this loosely, matching the `any[]`
 *  pose_data payload from videoApi.ts). `landmarks` is optional because
 *  callers (e.g. EvaluationResults.tsx) may pass frames before pose data has
 *  fully loaded; useMultiTrajectoryTrails below needs it non-optional, so its
 *  inputs are normalized via templatePoseDataForTrails/athletePoseDataForTrails. */
interface PoseFrame {
  landmarks?: Array<{ x: number; y: number; z?: number; visibility?: number }>;
}

type SyncMode = 'frame' | 'phase';

export interface EnhancedEvaluationDisplayProps {
  templateVideoUrl: string;
  /** Blob URL, server URL, or null when the ephemeral video is no longer available */
  athleteVideoUrl: string | null;
  frameScores?: FrameScore[];
  athleteRegionHulls?: RegionHull[];
  templateRegionHulls?: RegionHull[];
  fps?: number;
  templateFps?: number;  // FPS for template video (defaults to fps)
  athleteFps?: number;   // FPS for athlete video (defaults to fps)
  /** Template trim window, in SECONDS of the FULL reference video's timeline.
   *  When the reference video was trimmed, scoring only covers [start, end); this
   *  clamps the TEMPLATE <video> so playback loops within the scored range.
   *  `templateWindowEndSec === null` means "to the end of the clip" (start-only trim).
   *  Full-video / absent (start 0 and end null) → no clamping; playback is unchanged.
   *  The ATHLETE video is a separately-trimmed file starting at 0 and is never offset. */
  templateWindowStartSec?: number;
  templateWindowEndSec?: number | null;
  phaseMapping?: PhaseMapping | null; // Phase mapping for phase-aligned sync
  // Pose visualization data (optional - for new visualization modes)
  /** RAW template-frame landmarks — the template panel's own skeleton.
      NEVER pre-mirrored (the template video is the reference frame). */
  templateLandmarks?: Array<{ x: number; y: number; visibility?: number }>;
  /** Ghost variant — mirrored iff the eval mirrored the athlete, so the ghost
      overlaid ON the athlete video matches the athlete's orientation.
      Falls back to templateLandmarks when absent (non-mirrored evals). */
  templateLandmarksGhost?: Array<{ x: number; y: number; visibility?: number }>;
  athleteLandmarks?: Array<{ x: number; y: number; visibility?: number }>;
  landmarkDeviations?: LandmarkDeviationWithCoords[];
  mirrored?: boolean;
  /**
   * Lead side for the TEMPLATE panel ('left' | 'right' | null).
   * When known, landmark chips in the template Trace Pointes hero show functional
   * labels ("Lead Wrist (R)") instead of anatomical names. null → anatomical labels.
   * Derived from evaluation.metadata.mirroring_decision by the parent.
   */
  templateLeadSide?: LeadSide;
  /**
   * Lead side for the ATHLETE panel ('left' | 'right' | null).
   * Same semantics as templateLeadSide. Each panel uses its own lead side.
   */
  athleteLeadSide?: LeadSide;
  spatialGoalResults?: Record<string, SpatialGoalResult | AxisGoalResult | TimingGoalResult>;
  athletePoseData?: PoseFrame[];
  templatePoseData?: PoseFrame[];
  /** When true, shows a pulsing placeholder in the video card area while pose data is loading. */
  isPoseDataLoading?: boolean;
  className?: string;
  onFrameChange?: (frameIndex: number) => void;
  onPhaseChange?: (phaseIndex: number) => void;
  // 3D skeleton props
  templateJoints3D?: number[][] | null;
  athleteJoints3D?: number[][] | null;
  boneScores?: Map<number, number>;
  has3DData?: boolean;
  isGravityAligned?: boolean;
  /** When false, side-view depth inference is unreliable — renders an amber badge
   *  near the 3D viewer toggle. True/null/undefined → no badge. */
  pose3dDepthReliable?: boolean | null;
  // When true, trajectory trail is enabled by default on evaluation load
  trailEnabledByDefault?: boolean;
}

export interface EnhancedEvaluationDisplayRef {
  seekToFrame: (frameIndex: number) => void;
  play: () => void;
  pause: () => void;
  getCurrentFrame: () => number;
}

// ---------------------------------------------------------------------------
// Trail selectors — Trace Pointes controls
// (templateWindowSecondsFromFrames / nextTemplateWindowTime moved to
// templateWindowHelpers.ts — see import above)
// ---------------------------------------------------------------------------

const TRAIL_LANDMARKS = [
  { value: 0,  label: 'Nose (Head)' },
  { value: 11, label: 'L Shoulder' },
  { value: 12, label: 'R Shoulder' },
  { value: 13, label: 'L Elbow' },
  { value: 14, label: 'R Elbow' },
  { value: 15, label: 'L Wrist' },
  { value: 16, label: 'R Wrist' },
  { value: 23, label: 'L Hip' },
  { value: 24, label: 'R Hip' },
  { value: 25, label: 'L Knee' },
  { value: 26, label: 'R Knee' },
  { value: 27, label: 'L Ankle' },
  { value: 28, label: 'R Ankle' },
];

// SingleTrailSelector was removed in the per-panel overlay split (2026-07-27).
// The template panel now uses the same MultiTrailSelector as the athlete (Trace Pointes hero).

/**
 * Trace Pointes trail style — the viewer's Ribbon / Dots switch.
 *
 * 'ribbon' is the approved Photon Trace light treatment (photonTrace.ts);
 * 'dots' is the dotted trail. The choice is ONE viewer-wide preference
 * (both panels, remembered in this browser under TRAIL_STYLE_STORAGE_KEY).
 * TRAIL_STYLE_DEFAULT applies only when nothing is saved — a proposed Ribbon
 * default, never an override of a saved choice (handoff OBJECTIVE.md).
 * Switching style re-renders the trail only: media time, selected landmarks,
 * persistence, scores and alignment are untouched.
 */
export type TrailStyle = 'dots' | 'ribbon';
export const TRAIL_STYLE_DEFAULT: TrailStyle = 'dots';
export const TRAIL_STYLE_STORAGE_KEY = 'sportform.tracePointes.style';
/**
 * Owner ruling 2026-11-02 after seeing Photon Trace on real prod footage:
 * "the ribbon is awful looking. Let's stick to the dots for now and hide the
 * ribbon effect; retain the capability so we can iterate on it in the future."
 * While false: the Ribbon | Dots control is not rendered, every viewer draws
 * dots regardless of a saved preference, and the renderer stays in the
 * bundle untouched (photonTrace.ts + the overlay's 'ribbon' branch + tests).
 * Flip to true to bring the switch back; TRAIL_STYLE_DEFAULT then decides
 * what a viewer with no saved preference sees.
 */
export const TRAIL_STYLE_SWITCH_ENABLED = false;

export function readStoredTrailStyle(): TrailStyle {
  if (!TRAIL_STYLE_SWITCH_ENABLED) return 'dots';
  try {
    const v = window.localStorage.getItem(TRAIL_STYLE_STORAGE_KEY);
    return v === 'dots' || v === 'ribbon' ? v : TRAIL_STYLE_DEFAULT;
  } catch {
    return TRAIL_STYLE_DEFAULT;
  }
}

export function writeStoredTrailStyle(style: TrailStyle): void {
  try {
    window.localStorage.setItem(TRAIL_STYLE_STORAGE_KEY, style);
  } catch {
    /* private mode / blocked storage — the in-memory choice still applies */
  }
}

/**
 * Playback alignment — "Natural speed" (SyncMode 'frame') vs "Match coach's
 * timing" (SyncMode 'phase'). Plain-language labels only; see the segmented
 * control below for the coach-facing copy and titles.
 *
 * Default is Natural speed — owner ruling 2026-11-02 (docs/specs/playback-modes.md,
 * V-2): both clips play at their real pace and start together, matching the
 * industry convention; tempo differences stay visible. The choice is
 * remembered per browser under PLAYBACK_ALIGNMENT_STORAGE_KEY, same
 * try/catch pattern as the Trace Pointes trail style above. A missing or
 * unrecognized stored value falls back to the default rather than throwing.
 */
export const PLAYBACK_ALIGNMENT_DEFAULT: SyncMode = 'frame';
export const PLAYBACK_ALIGNMENT_STORAGE_KEY = 'sportform.playback.alignment';

export function readStoredPlaybackAlignment(): SyncMode {
  try {
    const v = window.localStorage.getItem(PLAYBACK_ALIGNMENT_STORAGE_KEY);
    return v === 'frame' || v === 'phase' ? v : PLAYBACK_ALIGNMENT_DEFAULT;
  } catch {
    return PLAYBACK_ALIGNMENT_DEFAULT;
  }
}

export function writeStoredPlaybackAlignment(mode: SyncMode): void {
  try {
    window.localStorage.setItem(PLAYBACK_ALIGNMENT_STORAGE_KEY, mode);
  } catch {
    /* private mode / blocked storage — the in-memory choice still applies */
  }
}


/** Preference order for auto-picking trail landmarks: fast-moving distal joints
 *  first (they produce the most informative traces). */
const TRAIL_DEFAULT_PREFERENCE = [16, 15, 28, 27, 14, 13, 26, 25, 12, 11, 0];
/** Mean-visibility floor below which a landmark is considered "not visible in
 *  this video" (e.g. ankles in a waist-up framing). */
const TRAIL_VIS_FLOOR = 0.4;

/** Per-landmark mean visibility across the clip (undefined visibility counts
 *  as fully visible, matching the pipeline convention). */
function meanVisibilityByLandmark(
  poseData: Array<{ landmarks?: Array<{ visibility?: number }> }> | null | undefined,
): Map<number, number> {
  const sums = new Map<number, { s: number; n: number }>();
  // Defensive: while pose extraction is in flight the API layer may briefly
  // yield a non-array (2026-07-29 crash: {status:"processing"} body). Never
  // iterate anything that isn't a frames array.
  if (!Array.isArray(poseData)) return new Map();
  for (const frame of poseData) {
    const lms = frame?.landmarks;
    if (!lms) continue;
    for (const { value } of TRAIL_LANDMARKS) {
      const v = lms[value]?.visibility;
      const cur = sums.get(value) ?? { s: 0, n: 0 };
      cur.s += v === undefined ? 1 : v;
      cur.n += 1;
      sums.set(value, cur);
    }
  }
  const out = new Map<number, number>();
  for (const [k, { s, n }] of sums) out.set(k, n > 0 ? s / n : 0);
  return out;
}

/** Pick 2 default trail landmarks that are actually VISIBLE in this clip.
 *  Returns null when the standard default [16, 28] is fine (both visible) —
 *  callers keep the default in that case. (Regression fix 2026-07-29: a
 *  waist-up athlete video has no visible ankle, so the default R-Ankle trace
 *  was empty and Trace Pointes appeared to "not work".) */
function pickVisibleTrailDefaults(
  meanVis: Map<number, number>,
): number[] | null {
  if (meanVis.size === 0) return null;
  const vis16 = meanVis.get(16) ?? 0;
  const vis28 = meanVis.get(28) ?? 0;
  if (vis16 >= TRAIL_VIS_FLOOR && vis28 >= TRAIL_VIS_FLOOR) return null;
  const visible = TRAIL_DEFAULT_PREFERENCE.filter(
    (v) => (meanVis.get(v) ?? 0) >= TRAIL_VIS_FLOOR,
  );
  if (visible.length >= 2) return visible.slice(0, 2);
  // Degenerate clip: fewer than 2 landmarks pass the floor — take the two
  // highest-visibility landmarks overall rather than showing nothing.
  const ranked = [...meanVis.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  return ranked.slice(0, 2);
}

/**
 * Multi-landmark trail selector used for BOTH the athlete and template panels.
 * Up to MAX_TRAILS (5) landmarks with per-landmark color swatches.
 * Default: 2 landmarks selected (lead wrist + lead ankle).
 *
 * FUNCTIONAL CHIP LABELS (2026-07-30):
 *   When `panelLeadSide` is known, side-paired landmarks show functional labels:
 *   "Lead Wrist (R)" / "Trail Wrist (L)" instead of "R Wrist" / "L Wrist".
 *   Lead-side chips are ordered FIRST within each joint pair (Req 2 ordering).
 *   Nose (Head) is always unchanged.
 *   When panelLeadSide is null, all labels stay anatomical (byte-identical to old UI).
 *
 * PERSIST|FADE TOGGLE (2026-07-28 prototype):
 *   Replaced the single "Persist" button with a two-segment Persist|Fade control.
 *   Persist active = full trace from clip start; Fade active = moving fade window.
 *   aria-label "Persist" / "Fade" for tests (replaces old "Persist" name).
 */
function MultiTrailSelector({
  enabled,
  onToggle,
  selectedIndices,
  onSelectionChange,
  persistToEnd,
  onPersistToggle,
  trailStyle,
  onTrailStyleChange,
  lowVisibilityValues = [],
  panelLeadSide = null,
}: {
  enabled: boolean;
  onToggle: () => void;
  selectedIndices: number[];
  onSelectionChange: (indices: number[]) => void;
  persistToEnd: boolean;
  onPersistToggle: () => void;
  /** Trail style switch (Ribbon | Dots) — one viewer-wide preference. */
  trailStyle: TrailStyle;
  onTrailStyleChange: (style: TrailStyle) => void;
  /** Landmark values with low mean visibility in this clip — chips are dimmed
   *  with an explanatory title (landmark out of frame / occluded). */
  lowVisibilityValues?: number[];
  /**
   * The panel's lead side ('left' | 'right' | null).
   * When provided and non-null, paired-joint chips render functional labels
   * (e.g. "Lead Wrist (R)") and are sorted lead-first within each pair.
   * null → anatomical labels, original order.
   */
  panelLeadSide?: LeadSide;
}) {
  const toggleLandmark = (landmarkValue: number) => {
    if (selectedIndices.includes(landmarkValue)) {
      // Remove it (always keep at least 1)
      if (selectedIndices.length > 1) {
        onSelectionChange(selectedIndices.filter(i => i !== landmarkValue));
      }
    } else {
      // Add it (cap at MAX_TRAILS)
      if (selectedIndices.length < MAX_TRAILS) {
        onSelectionChange([...selectedIndices, landmarkValue]);
      }
    }
  };

  return (
    <div
      className="flex flex-col gap-1.5 px-3 py-2 bg-[#0A0E12] border-t border-white/[0.04]"
      data-testid="multi-trail-selector"
    >
      {/* Top row: toggle + persist|fade segmented control */}
      <div className="flex items-center gap-2">
        <button
          onClick={onToggle}
          className={[
            'px-2 py-1 rounded text-[11px] font-medium transition-colors border',
            enabled
              ? 'bg-[#191F2B] border-white/20 text-white'
              : 'bg-dark-elevated-2 border-border-subtle text-text-muted hover:text-text-primary',
          ].join(' ')}
          aria-pressed={enabled}
          aria-label="Toggle Trace Pointes"
        >
          <span
            className={[
              'inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle',
              enabled ? 'bg-emerald-400' : 'bg-white/25',
            ].join(' ')}
            aria-hidden="true"
          />
          {enabled ? 'On' : 'Off'}
        </button>
        {enabled && (
          <>
            <span className="text-[10px] text-text-muted/60 select-none">
              {selectedIndices.length}/{MAX_TRAILS}
            </span>
            <span className="flex-1" />
            {/* Ribbon | Dots — the trail STYLE switch (Photon Trace handoff,
                docs/trace-pointes/handoff/). Same segmented-control markup
                and keyboard/touch behaviour as Persist | Fade beside it; one
                viewer-wide preference remembered in the browser. Hidden while
                TRAIL_STYLE_SWITCH_ENABLED is false (owner ruling 2026-11-02). */}
            {TRAIL_STYLE_SWITCH_ENABLED && (
            <div
              className="flex items-center rounded border border-white/[0.08] overflow-hidden mr-2"
              role="group"
              aria-label="Trail style"
            >
              <button
                type="button"
                onClick={() => { if (trailStyle !== 'ribbon') onTrailStyleChange('ribbon'); }}
                aria-pressed={trailStyle === 'ribbon'}
                aria-label="Ribbon"
                title="Continuous light trace"
                className={[
                  'px-2.5 py-1 text-[10px] font-medium tracking-wide transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                  trailStyle === 'ribbon'
                    ? 'bg-white/[0.10] text-text-primary'
                    : 'bg-transparent text-text-muted hover:text-text-secondary',
                ].join(' ')}
              >
                Ribbon
              </button>
              <span className="w-px h-3.5 bg-white/[0.08] flex-shrink-0" aria-hidden="true" />
              <button
                type="button"
                onClick={() => { if (trailStyle !== 'dots') onTrailStyleChange('dots'); }}
                aria-pressed={trailStyle === 'dots'}
                aria-label="Dots"
                title="Dotted trail"
                className={[
                  'px-2.5 py-1 text-[10px] font-medium tracking-wide transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                  trailStyle === 'dots'
                    ? 'bg-white/[0.10] text-text-primary'
                    : 'bg-transparent text-text-muted hover:text-text-secondary',
                ].join(' ')}
              >
                Dots
              </button>
            </div>
            )}
            {/*
             * Persist|Fade segmented toggle (2026-07-28 prototype refinement).
             * Two segments in a pill-shaped container. Persist active = full
             * trace visible; Fade active = moving fade window.
             * aria-pressed on each segment for accessibility and existing tests
             * (tests find by role "button" with name /persist/i or /fade/i).
             */}
            <div
              className="flex items-center rounded border border-white/[0.08] overflow-hidden"
              role="group"
              aria-label="Trail display mode"
            >
              <button
                type="button"
                onClick={() => { if (!persistToEnd) onPersistToggle(); }}
                aria-pressed={persistToEnd}
                aria-label="Persist"
                title="Show full trail from start of clip"
                className={[
                  'px-2.5 py-1 text-[10px] font-medium tracking-wide transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                  persistToEnd
                    ? 'bg-white/[0.10] text-text-primary'
                    : 'bg-transparent text-text-muted hover:text-text-secondary',
                ].join(' ')}
              >
                Persist
              </button>
              <span className="w-px h-3.5 bg-white/[0.08] flex-shrink-0" aria-hidden="true" />
              <button
                type="button"
                onClick={() => { if (persistToEnd) onPersistToggle(); }}
                aria-pressed={!persistToEnd}
                aria-label="Fade"
                title="Show fade window — trail fades as you advance"
                className={[
                  'px-2.5 py-1 text-[10px] font-medium tracking-wide transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                  !persistToEnd
                    ? 'bg-white/[0.10] text-text-primary'
                    : 'bg-transparent text-text-muted hover:text-text-secondary',
                ].join(' ')}
              >
                Fade
              </button>
            </div>
          </>
        )}
      </div>

      {/* Landmark chip row — only when enabled.
          Chips refined to prototype: rounded dot-chips with colored dot + label.
          When panelLeadSide is known, chips are sorted lead-first within each pair
          and display functional labels ("Lead Wrist (R)") instead of anatomical ones.
          The order within the TRAIL_LANDMARKS array is preserved except that within
          each joint pair the lead-side chip is placed before the trail-side chip. */}
      {enabled && (() => {
        // Build a display list: compute functional label and sort lead-first when
        // lead side is known. TRAIL_LANDMARKS has pairs in (L, R) order: 11,12,
        // 13,14, 15,16, 23,24, 25,26, 27,28. Index 0 (Nose) is singleton.
        // Re-order: for each pair, put the lead-side landmark first.
        const withLabels = TRAIL_LANDMARKS.map(lm => ({
          ...lm,
          displayLabel: functionalChipLabel(lm.value, lm.label, panelLeadSide),
        }));

        let orderedLandmarks = withLabels;
        if (panelLeadSide !== null) {
          // Pairs by base joint (contiguous in TRAIL_LANDMARKS): [11,12],[13,14],
          // [15,16],[23,24],[25,26],[27,28]. Nose (0) is always first (singleton).
          // We reconstruct the ordered list: nose first, then each pair lead-first.
          const nose = withLabels.find(l => l.value === 0);
          const pairsInOrder: [number, number][] = [
            [11, 12], [13, 14], [15, 16], [23, 24], [25, 26], [27, 28],
          ];
          const reordered: typeof withLabels = nose ? [nose] : [];
          for (const [leftIdx, rightIdx] of pairsInOrder) {
            const leftLm  = withLabels.find(l => l.value === leftIdx);
            const rightLm = withLabels.find(l => l.value === rightIdx);
            if (!leftLm || !rightLm) continue;
            // Lead side determines which goes first.
            if (panelLeadSide === 'left') {
              reordered.push(leftLm, rightLm);
            } else {
              reordered.push(rightLm, leftLm);
            }
          }
          orderedLandmarks = reordered;
        }

        return (
          <div
            className="flex flex-wrap gap-1"
            role="group"
            aria-label="Select up to 5 landmarks to trace"
          >
            {orderedLandmarks.map((lm) => {
              const slotIdx = selectedIndices.indexOf(lm.value);
              const isSelected = slotIdx !== -1;
              const palette = isSelected ? TRAIL_LANDMARK_PALETTE[slotIdx % TRAIL_LANDMARK_PALETTE.length] : null;
              const canAdd = !isSelected && selectedIndices.length < MAX_TRAILS;
              const lowVis = lowVisibilityValues.includes(lm.value);
              // Use displayLabel for user-visible text/title; anatomical lm.label for
              // title fallback when low-vis (it still names the joint clearly).
              const chipLabel = lm.displayLabel;

              return (
                <button
                  key={lm.value}
                  onClick={() => toggleLandmark(lm.value)}
                  disabled={!isSelected && !canAdd}
                  aria-pressed={isSelected}
                  data-low-visibility={lowVis || undefined}
                  title={
                    lowVis
                      ? `${chipLabel} is rarely visible in this video — trace may be empty`
                      : isSelected
                      ? `Remove ${chipLabel}`
                      : canAdd
                      ? `Add ${chipLabel}`
                      : `Max ${MAX_TRAILS} landmarks selected`
                  }
                  className={[
                    'flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                    'border transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
                    isSelected
                      ? 'text-white border-white/20'
                      : canAdd
                      ? 'bg-dark-elevated-2 border-border-subtle text-text-muted hover:text-text-secondary hover:border-white/[0.10] cursor-pointer'
                      : 'bg-dark-elevated-2 border-border-subtle text-text-muted/40 cursor-not-allowed opacity-50',
                    lowVis && !isSelected ? 'opacity-40' : '',
                  ].join(' ')}
                  style={isSelected && palette ? { background: palette.hex + '22', borderColor: palette.hex + '77' } : undefined}
                  data-landmark-chip={lm.value}
                  data-selected={isSelected}
                >
                  {/* Color swatch */}
                  {isSelected && palette && (
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: palette.hex }}
                      aria-hidden="true"
                    />
                  )}
                  {chipLabel}
                </button>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}


const EnhancedEvaluationDisplay = forwardRef<
  EnhancedEvaluationDisplayRef,
  EnhancedEvaluationDisplayProps
>(
  (
    {
      templateVideoUrl,
      athleteVideoUrl,
      frameScores = [],
      athleteRegionHulls = [],
      templateRegionHulls = [],
      fps = 30,
      templateFps,
      athleteFps,
      templateWindowStartSec,
      templateWindowEndSec,
      phaseMapping,
      templateLandmarks,
      templateLandmarksGhost,
      athleteLandmarks,
      landmarkDeviations,
      mirrored,
      templateLeadSide = null,
      athleteLeadSide = null,
      spatialGoalResults,
      athletePoseData,
      templatePoseData,
      isPoseDataLoading,
      className,
      onFrameChange,
      onPhaseChange,
      templateJoints3D,
      athleteJoints3D,
      boneScores,
      has3DData,
      isGravityAligned,
      pose3dDepthReliable,
      trailEnabledByDefault,
    },
    ref
  ) => {
    // Resolve FPS values
    const effectiveTemplateFps = templateFps ?? fps;
    const effectiveAthleteFps = athleteFps ?? fps;

    // Determine if phase sync is available and set default sync mode
    const hasPhaseData = phaseMapping && phaseMapping.phase_pairs.length > 0;
    // Default to Natural speed ('frame'); a saved choice in this browser wins
    // (readStoredPlaybackAlignment — see PLAYBACK_ALIGNMENT_STORAGE_KEY above).
    const [syncMode, setSyncModeState] = useState<SyncMode>(readStoredPlaybackAlignment);
    const setSyncMode = useCallback((mode: SyncMode) => {
      setSyncModeState(mode);
      writeStoredPlaybackAlignment(mode);
    }, []);
    const [playbackSpeed, setPlaybackSpeed] = useState(1.0);

    // 3D is unavailable for side-view templates: depth inference cannot reliably
    // resolve near-vs-far leg ordering, producing crossed-leg skeletons.
    // Gate is permissive: only hide when pose3dDepthReliable is explicitly false;
    // null/undefined (e.g. camera view unresolved) preserves 3D access.
    const threeDAvailable = ENABLE_3D_VISUALIZATION && !!has3DData && pose3dDepthReliable !== false;

    // ---------------------------------------------------------------------------
    // Overlay state — per-panel (Slice 1f + per-panel split 2026-07-27).
    //
    // overlayState        → ATHLETE panel (pose/ghost/trails/goals/regions/viewMode)
    // templateOverlayState → TEMPLATE panel (pose/ghost/trails/goals/regions)
    //
    // viewMode (2d/3d) is GLOBAL — it lives only on overlayState.viewMode.
    // templateOverlayState.viewMode is kept for type compatibility but is never read
    // for view switching; the template panel never has a 2D/3D toggle of its own.
    //
    // All four overlay pills (Pose, Ghost, Goals, Regions) on the template panel
    // control ONLY template-panel overlays. The athlete panel overlays remain
    // controlled exclusively by overlayState.
    // ---------------------------------------------------------------------------
    // Trace Pointes default flipped from OPT-OUT to OPT-IN (owner ruling
    // 2026-08-03: "only activate Pose by default, others off").
    //
    // Was: `trailEnabledByDefault !== false` — on unless explicitly false. Since
    // the backend only ever emits this key as `true` (evaluation_pipeline.py
    // ~5603 writes it only under a truthiness check) and NOTHING in the product
    // writes it at all, `undefined` was the universal case and trails were
    // therefore always on. Now a template must explicitly opt in.
    const trailsDefault = trailEnabledByDefault === true;

    const [overlayState, setOverlayState] = useState<EvalOverlayState>({
      ...DEFAULT_OVERLAY_STATE,
      trails: trailsDefault,
    });

    // Template panel gets its own independent overlay state.
    // ghost on the template panel renders a translucent style of the template skeleton
    // (not a comparison overlay) — see comment at the PoseOverlay usage below.
    const [templateOverlayState, setTemplateOverlayState] = useState<EvalOverlayState>({
      ...DEFAULT_OVERLAY_STATE,
      trails: trailsDefault,
      // viewMode is unused on the template panel (global, lives on overlayState)
    });

    // ── Pose / Ghost are MUTUALLY EXCLUSIVE (owner ruling 2026-08-03) ────────
    // "Pose+Ghost together showing both sounds very confusing. Let's have them
    // do their separate things."
    //
    // They share a single canvas driven by one exclusive `vizConfig.mode`, so
    // enabling both could never composite — the ghost simply won and Pose went
    // silent, which read as a broken pill. Rather than fake a combined mode,
    // the CONTROL layer now enforces the exclusivity that the render layer
    // already had: turning one on turns the other off. Both-off is still valid
    // (clean video).
    //
    // The reducers live in EvalOverlayControlBar (togglePoseBit/toggleGhostBit)
    // so the unit tests exercise the real implementation. Bound once here and
    // shared by the portrait bar and the landscape bar so the two cannot drift.
    const togglePose = useCallback(() => setOverlayState(togglePoseBit), []);
    const toggleGhost = useCallback(() => setOverlayState(toggleGhostBit), []);

    // ── (B-2026-0130) Per-column landscape bars supersede the old sheet/strip. ──
    // landscapeSheetOpen / landscapeSheetPanel removed — always-visible per-column
    // bars are now rendered directly inside each column (see below).

    // Warm the 3D viewer chunk in the background so the first 2D→3D toggle is
    // instant once data is available. The dynamic import returns a cached
    // Promise on subsequent calls.
    useEffect(() => {
      if (has3DData && ENABLE_3D_VISUALIZATION) {
        _prefetchSkeletonViewer3D();
      }
    }, [has3DData]);

    // Side-view guard: if 3D becomes unavailable (e.g. prop changes or initial
    // render for a side-view eval), force back to 2D so the user is never stuck
    // on a hidden/broken 3D canvas.
    useEffect(() => {
      if (!threeDAvailable) {
        setOverlayState(s => s.viewMode !== '2d' ? { ...s, viewMode: '2d' } : s);
      }
    }, [threeDAvailable]);

    // Video unavailability state — set when the browser fails to load a video src
    const [athleteVideoUnavailable, setAthleteVideoUnavailable] = useState(false);
    const [templateVideoUnavailable, setTemplateVideoUnavailable] = useState(false);

    // Trajectory trail — Trace Pointes state per video panel.
    // Both panels now use multi-landmark trails (up to MAX_TRAILS each).
    // Template panel: multi-landmark (same as athlete — the full Trace Pointes hero)
    // Default 2: lead wrist (16) + lead ankle (28), matching design D3.
    const [templateTrailIndices, setTemplateTrailIndices] = useState<number[]>([16, 28]);
    // Persist-by-default (Trace Pointes spec): the full dotted trace stays on
    // screen so the pointes are visible immediately, even on a paused video at
    // frame 0. Toggle off for a moving fade-window. (Regression fix 2026-07-27:
    // default was false → paused video showed no trace at all.)
    const [templateTrailPersist, setTemplateTrailPersist] = useState(true);

    // Athlete panel: multi-landmark (the Trace Pointes centerpiece)
    // Default 2: lead wrist (16) + lead ankle (28), matching design D3.
    const [athleteTrailIndices, setAthleteTrailIndices] = useState<number[]>([16, 28]);
    // Persist-by-default — see templateTrailPersist note above.
    const [athleteTrailPersist, setAthleteTrailPersist] = useState(true);
    // Trail style (Ribbon | Dots) — one viewer-wide preference, remembered in
    // this browser; Ribbon only when nothing is saved (TRAIL_STYLE_DEFAULT).
    const [trailStyle, setTrailStyleState] = useState<TrailStyle>(readStoredTrailStyle);
    const setTrailStyle = useCallback((style: TrailStyle) => {
      setTrailStyleState(style);
      writeStoredTrailStyle(style);
    }, []);

    // ── Smart trail defaults (regression fix 2026-07-29; lead-side awareness 2026-07-30) ──
    // When the clip's framing hides the standard defaults (e.g. waist-up
    // athlete video → no visible ankle), auto-pick the two most-visible
    // preferred landmarks instead — otherwise Trace Pointes renders nothing
    // and looks broken. Only applies while the user hasn't touched the
    // selection; a manual change wins permanently.
    //
    // Lead-side awareness (2026-07-30, Req 3):
    //   When the panel's lead side is known, prefer LEAD wrist + LEAD ankle
    //   (still visibility-gated). Template and athlete panels each use their own
    //   lead side. Falls back to pickVisibleTrailDefaults() when lead-side
    //   defaults are not available (ankle not visible, lead side unknown, etc.).
    const athleteTrailTouched = useRef(false);
    const templateTrailTouched = useRef(false);
    const athleteMeanVis = useMemo(
      () => meanVisibilityByLandmark(athletePoseData), [athletePoseData]);
    const templateMeanVis = useMemo(
      () => meanVisibilityByLandmark(templatePoseData), [templatePoseData]);
    useEffect(() => {
      if (athleteTrailTouched.current) return;
      // Try lead-side-aware defaults first.
      const leadPicked = pickLeadSideDefaults(athleteLeadSide, athleteMeanVis, TRAIL_VIS_FLOOR);
      if (leadPicked) { setAthleteTrailIndices(leadPicked); return; }
      // Fall back to visibility-based defaults (handles waist-up framing etc.).
      const picked = pickVisibleTrailDefaults(athleteMeanVis);
      if (picked) setAthleteTrailIndices(picked);
    }, [athleteMeanVis, athleteLeadSide]);
    useEffect(() => {
      if (templateTrailTouched.current) return;
      // Try lead-side-aware defaults first.
      const leadPicked = pickLeadSideDefaults(templateLeadSide, templateMeanVis, TRAIL_VIS_FLOOR);
      if (leadPicked) { setTemplateTrailIndices(leadPicked); return; }
      // Fall back to visibility-based defaults.
      const picked = pickVisibleTrailDefaults(templateMeanVis);
      if (picked) setTemplateTrailIndices(picked);
    }, [templateMeanVis, templateLeadSide]);

    // Derive per-panel trail enabled state from the respective overlay state.
    // Template panel is controlled by templateOverlayState.trails (independent).
    const templateTrailEnabled = templateOverlayState.trails;
    const athleteTrailEnabled = overlayState.trails;

    // Phase sync hook
    const phaseSync = usePhaseSync({
      phaseMapping,
      templateFps: effectiveTemplateFps,
      athleteFps: effectiveAthleteFps,
      onPhaseChange: (phaseIndex) => {
        onPhaseChange?.(phaseIndex);
      },
    });
    const { opacity } = useBodyRegionOverlay();

    // Visualization mode hook — driven by the EvalOverlayControlBar's ghost pill (RF#1).
    // Ghost ON → mode='ghost_echo'; Ghost OFF → mode='off'.
    // No VisualizationModeSelector is rendered; the hook is kept for its setMode side-effects
    // (intensity bookkeeping stays inside the hook).
    const {
      config: vizConfig,
      setMode,
    } = useVisualizationMode({
      // Seeded from the SAME resolver the sync effect uses — see
      // overlayBitsToVizMode. Seeding from the ghost bit alone was only
      // accidentally correct while the default was ghost:true.
      mode: overlayBitsToVizMode(DEFAULT_OVERLAY_STATE.pose, DEFAULT_OVERLAY_STATE.ghost),
    });

    // Keep vizConfig.mode in sync when overlayState.ghost or overlayState.pose changes.
    //
    // GHOST IS INDEPENDENT (2026-08-02 owner ruling): Ghost used to require
    // Pose — `if (!overlayState.pose) targetMode = 'off'` meant turning Ghost on
    // with Pose off rendered NOTHING, and the pills became order-dependent
    // (Ghost→Pose worked, Pose→Ghost did not).
    //
    // As of 2026-08-03 togglePose/toggleGhost keep the two mutually exclusive,
    // so `ghost && pose` is unreachable from the UI. The full mapping lives in
    // overlayBitsToVizMode so this effect and the initial seed above cannot
    // drift apart.
    useEffect(() => {
      const targetMode = overlayBitsToVizMode(overlayState.pose, overlayState.ghost);
      if (vizConfig.mode !== targetMode) {
        setMode(targetMode);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayState.pose, overlayState.ghost]);

    // Trajectory trail data — Trace Pointes.
    // useMultiTrajectoryTrails requires `landmarks` on every frame; PoseFrame
    // leaves it optional (frames may arrive before pose data fully loads), so
    // default to an empty array per frame here rather than loosening the
    // hook's stricter contract.
    const templatePoseDataForTrails = useMemo(
      () => templatePoseData?.map((f) => ({ landmarks: f.landmarks ?? [] })) ?? null,
      [templatePoseData]
    );
    const athletePoseDataForTrails = useMemo(
      () => athletePoseData?.map((f) => ({ landmarks: f.landmarks ?? [] })) ?? null,
      [athletePoseData]
    );

    // Template: up to MAX_TRAILS (5) landmarks, independent of athlete selection.
    // Athlete: up to MAX_TRAILS (5) landmarks, all drawn in one RAF pass.
    const templateTrailConfigs = useMultiTrajectoryTrails(
      templatePoseDataForTrails,
      templateTrailIndices,
      effectiveTemplateFps,
      templateTrailEnabled,
    );
    const athleteTrailConfigs = useMultiTrajectoryTrails(
      athletePoseDataForTrails,
      athleteTrailIndices,
      effectiveAthleteFps,
      athleteTrailEnabled,
    );

    // Video players
    const templatePlayer = useVideoPlayer({
      fps: effectiveTemplateFps,
      onTimeUpdate: (time, frame) => {
        if (syncMode === 'phase' && phaseSync.hasPhaseData) {
          // Phase-aligned sync: calculate progress from template time, then get athlete time
          const phaseInfo = phaseSync.calculateProgressFromTemplateTime(time);
          if (phaseInfo && athletePlayer.videoRef.current) {
            // Update phase sync state
            if (phaseInfo.phaseIndex !== phaseSync.currentPhaseIndex) {
              // Use updatePhaseIndex for seamless auto-transitions (doesn't reset progress)
              phaseSync.updatePhaseIndex(phaseInfo.phaseIndex);
            }
            phaseSync.setPhaseProgress(phaseInfo.progress);

            // Drive the athlete toward the phase-mapped position. While
            // playing, its playbackRate follows the phase's timing ratio and a
            // seek corrects only real drift; while paused the seek is exact.
            // (2026-11-02: hard-seeking every tick fought native playback when
            // the ratio was far from 1 — see utils/phaseSyncDrive.ts.)
            const athleteVideo = athletePlayer.videoRef.current;
            const athleteTime = phaseSync.getAthleteTime(phaseInfo.progress, phaseInfo.phaseIndex);
            const plan = planAthleteDrive({
              playing: !athleteVideo.paused,
              playbackSpeed,
              timingRatio: phaseSync.getTimingRatioForPhase(phaseInfo.phaseIndex),
              athleteTime: athleteVideo.currentTime,
              targetTime: athleteTime,
            });
            if (Math.abs(athleteVideo.playbackRate - plan.rate) > 1e-3) {
              athleteVideo.playbackRate = plan.rate;
            }
            if (plan.seekTo !== null) athletePlayer.seek(plan.seekTo);
          }
        } else if (athletePlayer.videoRef.current) {
          // Frame-aligned sync: seek to same time
          athletePlayer.seek(time);
        }
        // Notify parent of frame change
        onFrameChange?.(frame);
      },
    });

    const athletePlayer = useVideoPlayer({ fps: effectiveAthleteFps });

    // Keep playback position stable across a signed-URL refresh
    // (backlog/media-url-refresh-playback-position.md). A no-op for the
    // empty→real first-render transition the `.load()` effect above
    // handles. The template trim-window clamp effect below is window-aware
    // on `loadedmetadata`: a restored position inside the window is kept,
    // one outside it is pulled back to the window start.
    useStableMediaSrc(templatePlayer.videoRef, templateVideoUrl);
    useStableMediaSrc(athletePlayer.videoRef, athleteVideoUrl);

    // ─────────────────────────────────────────────────────────────────────────
    // PER-PANEL LANDMARK EXTRACTION (2026-08-02 — owner report on prod)
    //
    // Each panel MUST index its own pose data by its OWN player's frame. The
    // page used to compute all three landmark sets from a single page-level
    // `currentFrame`, which is fed by templatePlayer's onTimeUpdate — i.e. it is
    // the TEMPLATE's frame — and then ratio-mapped it as if it were the
    // ATHLETE's. Two bugs fell out of that one confusion:
    //
    //  1. Template skeleton ran AHEAD of the template video ("it's moving before
    //     he does"): video at frame 42, skeleton drawn from 42/86*156 = frame 76.
    //  2. Athlete skeleton silently vanished: athletePoseData was indexed by the
    //     TEMPLATE frame, and extractLandmarksFromFrame returns undefined past
    //     the end of the array (athlete 87 frames vs template 157). With
    //     `athleteLandmarks` undefined, UnifiedPoseOverlay's
    //     `mode === 'differential_skeleton' && athleteLandmarks` gate never fired
    //     — while ghost_echo (which needs only templateLandmarks) still rendered.
    //     Hence "Pose alone shows nothing, but Ghost works".
    //
    // The ghost drawn over the athlete uses the TEMPLATE player's frame on
    // purpose: the two players are already held in correspondence by the sync
    // logic (time-locked in Frame Sync, phase-mapped in Phase Sync), so whatever
    // the template player is showing IS the corresponding template moment. That
    // is correct in BOTH sync modes and needs no ratio arithmetic.
    //
    // The `?? props` fallbacks keep callers that pass landmarks directly (and
    // the existing tests) working when pose data is absent.
    // ─────────────────────────────────────────────────────────────────────────
    const templateLandmarksOwn = useMemo(
      () => extractLandmarksFromFrame(templatePoseData, templatePlayer.currentFrame) ?? templateLandmarks,
      [templatePoseData, templatePlayer.currentFrame, templateLandmarks],
    );

    const athleteLandmarksOwn = useMemo(
      () => extractLandmarksFromFrame(athletePoseData, athletePlayer.currentFrame) ?? athleteLandmarks,
      [athletePoseData, athletePlayer.currentFrame, athleteLandmarks],
    );

    // Ghost = template pose in the ATHLETE's coordinate frame (mirrored when the
    // evaluation mirrored the athlete). Display cousin of the LFT-021 family: the
    // template panel takes the RAW pose, the ghost takes the mirrored one.
    const templateGhostOwn = useMemo(
      () => (mirrored ? mirrorLandmarks(templateLandmarksOwn) : templateLandmarksOwn)
        ?? templateLandmarksGhost ?? templateLandmarks,
      [templateLandmarksOwn, mirrored, templateLandmarksGhost, templateLandmarks],
    );

    // Proportional frame mapping for videos with different frame counts
    const mapFrameProportionally = useCallback((
      sourceFrame: number,
      sourceTotalFrames: number,
      targetTotalFrames: number
    ): number => {
      if (sourceTotalFrames <= 0 || targetTotalFrames <= 0) return sourceFrame;
      const progress = sourceFrame / sourceTotalFrames;
      return Math.round(progress * targetTotalFrames);
    }, []);

    // When the template video URL changes from empty to a real URL (which happens when
    // the evaluation data loads after the component has already mounted), the browser
    // does not automatically reload the video element — React updates the src attribute
    // in the DOM but the browser ignores it unless .load() is called explicitly.
    // This effect ensures the template video is reloaded whenever the URL becomes
    // available. It is intentionally limited to template videos (templates are
    // permanent server-hosted files; the hook resolves them asynchronously relative
    // to the evaluation data fetch, causing a first-render race condition).
    useEffect(() => {
      if (templateVideoUrl && templatePlayer.videoRef.current) {
        // Clear any previous error state so the "Video no longer available" overlay
        // doesn't persist when a real URL replaces the initial empty-string src.
        // (The empty-string src fires onerror immediately — this resets that false alarm.)
        setTemplateVideoUnavailable(false);
        templatePlayer.videoRef.current.load();
      }
    }, [templateVideoUrl, templatePlayer.videoRef]);

    // Template trim-window clamp (TVT-2, template-video trimming).
    // The reference video is stored/played in full, but only the scored range
    // [startSec, endSec) should be shown on the results page. Seek to the window
    // start on load and loop back to it when playback passes the window end.
    // Active only when the window is a real sub-range (start > 0 OR end != null);
    // for full-video templates (start 0, end null) this effect is inert and
    // playback is byte-identical to the pre-TVT behaviour. The athlete video is a
    // separate trimmed file and is intentionally NOT touched here.
    const templateWindowActive =
      (templateWindowStartSec ?? 0) > 0 ||
      (templateWindowEndSec !== undefined && templateWindowEndSec !== null);
    useEffect(() => {
      if (!templateWindowActive) return;
      const video = templatePlayer.videoRef.current;
      if (!video) return;

      const startSec = Math.max(0, templateWindowStartSec ?? 0);
      const endSec =
        templateWindowEndSec !== undefined && templateWindowEndSec !== null
          ? templateWindowEndSec
          : null;
      // Tolerance so floating-point drift near the window edges doesn't thrash.
      const EPS = 0.02;

      const seekToStart = () => {
        // Avoid redundant assignments (each reset would re-fire timeupdate).
        if (Math.abs(video.currentTime - startSec) > EPS) {
          video.currentTime = startSec;
        }
      };
      const onLoadedMetadata = () => {
        // Window-aware, not an unconditional seek-to-start: a fresh load
        // sits at 0 (before the window) and snaps to the start as before,
        // but a position `useStableMediaSrc` restored after a signed-URL
        // refresh that is already INSIDE the window is left alone — so the
        // coach keeps their scrub position instead of being kicked back to
        // the window start every ~9 minutes (Batch 5/6 red team, finding 3).
        const target = nextTemplateWindowTime(video.currentTime, startSec, endSec, EPS);
        if (target !== null) {
          video.currentTime = target;
        }
      };
      const enforceWindow = () => {
        const target = nextTemplateWindowTime(video.currentTime, startSec, endSec, EPS);
        if (target !== null && Math.abs(video.currentTime - target) > EPS) {
          video.currentTime = target;
        }
      };
      const onEnded = () => {
        // start-only trim (endSec null): native end reached → loop to window start.
        video.currentTime = startSec;
      };

      // Metadata may already be available (video loaded before this effect ran).
      if (video.readyState >= 1) {
        seekToStart();
      }
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('timeupdate', enforceWindow);
      video.addEventListener('ended', onEnded);
      return () => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('timeupdate', enforceWindow);
        video.removeEventListener('ended', onEnded);
      };
    }, [
      templateWindowActive,
      templateWindowStartSec,
      templateWindowEndSec,
      templatePlayer.videoRef,
      templateVideoUrl,
    ]);

    // Apply playback speed to both videos
    useEffect(() => {
      if (templatePlayer.videoRef.current) {
        templatePlayer.videoRef.current.playbackRate = playbackSpeed;
      }
      if (athletePlayer.videoRef.current) {
        athletePlayer.videoRef.current.playbackRate = playbackSpeed;
      }
    }, [playbackSpeed, templatePlayer.videoRef, athletePlayer.videoRef]);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        seekToFrame: (frameIndex: number) => {
          templatePlayer.seekToFrame(frameIndex);
          if (syncMode === 'frame') {
            // Map frame proportionally for videos with different frame counts
            const athleteFrame = mapFrameProportionally(
              frameIndex,
              templatePlayer.totalFrames,
              athletePlayer.totalFrames
            );
            athletePlayer.seekToFrame(athleteFrame);
          } else {
            athletePlayer.seekToFrame(frameIndex);
          }
        },
        play: () => {
          templatePlayer.play();
          athletePlayer.play();
        },
        pause: () => {
          templatePlayer.pause();
          athletePlayer.pause();
        },
        getCurrentFrame: () => templatePlayer.currentFrame,
      }),
      [templatePlayer, athletePlayer, syncMode, mapFrameProportionally]
    );

    // Sync play/pause
    const togglePlay = useCallback(() => {
      const athleteVideo = athletePlayer.videoRef.current;
      if (templatePlayer.isPlaying) {
        templatePlayer.pause();
        athletePlayer.pause();
        // Leave the athlete at the viewer speed for scrubbing / frame steps.
        if (athleteVideo) athleteVideo.playbackRate = playbackSpeed;
      } else {
        if (athleteVideo && syncMode === 'phase' && phaseSync.hasPhaseData) {
          // Start at the aligned pace of the phase the template is in so the
          // first ticks do not snap (phaseSyncDrive keeps it there per tick).
          const info = phaseSync.calculateProgressFromTemplateTime(templatePlayer.currentTime);
          const ratio = info ? phaseSync.getTimingRatioForPhase(info.phaseIndex) : 1;
          athleteVideo.playbackRate = clampPlaybackRate(playbackSpeed * ratio);
        }
        templatePlayer.play();
        athletePlayer.play();
      }
    }, [templatePlayer, athletePlayer, syncMode, phaseSync, playbackSpeed]);

    // Spacebar → play/pause (owner request 2026-07-30). Global listener with
    // guards: ignore when the user is typing (input/textarea/select/
    // contentEditable) or when a button/toggle has focus (Space would
    // double-trigger it). preventDefault stops the page from scrolling.
    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'Space' && e.key !== ' ') return;
        const el = e.target as HTMLElement | null;
        if (el) {
          const tag = el.tagName;
          if (
            tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
            tag === 'BUTTON' || el.isContentEditable ||
            el.getAttribute('role') === 'button'
          ) {
            return;
          }
        }
        e.preventDefault();
        togglePlay();
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [togglePlay]);

    // Frame navigation
    const nextFrame = useCallback(() => {
      const newTemplateFrame = templatePlayer.currentFrame + 1;
      templatePlayer.nextFrame();
      if (syncMode === 'frame') {
        // Map frame proportionally for videos with different frame counts
        const athleteFrame = mapFrameProportionally(
          newTemplateFrame,
          templatePlayer.totalFrames,
          athletePlayer.totalFrames
        );
        athletePlayer.seekToFrame(athleteFrame);
      } else {
        athletePlayer.nextFrame();
      }
    }, [templatePlayer, athletePlayer, syncMode, mapFrameProportionally]);

    const previousFrame = useCallback(() => {
      const newTemplateFrame = Math.max(0, templatePlayer.currentFrame - 1);
      templatePlayer.previousFrame();
      if (syncMode === 'frame') {
        // Map frame proportionally for videos with different frame counts
        const athleteFrame = mapFrameProportionally(
          newTemplateFrame,
          templatePlayer.totalFrames,
          athletePlayer.totalFrames
        );
        athletePlayer.seekToFrame(athleteFrame);
      } else {
        athletePlayer.previousFrame();
      }
    }, [templatePlayer, athletePlayer, syncMode, mapFrameProportionally]);

    // Seek both videos
    const handleSeek = useCallback(
      (time: number) => {
        templatePlayer.seek(time);
        if (syncMode === 'phase' && phaseSync.hasPhaseData) {
          // Phase sync: calculate corresponding athlete time
          const phaseInfo = phaseSync.calculateProgressFromTemplateTime(time);
          if (phaseInfo) {
            const athleteTime = phaseSync.getAthleteTime(phaseInfo.progress, phaseInfo.phaseIndex);
            athletePlayer.seek(athleteTime);
          }
        } else {
          athletePlayer.seek(time);
        }
      },
      [templatePlayer, athletePlayer, syncMode, phaseSync]
    );

    // Handle phase progress change from timeline slider
    const handlePhaseProgressChange = useCallback(
      (progress: number) => {
        phaseSync.setPhaseProgress(progress);
        // Seek both videos to the new progress position
        const templateTime = phaseSync.getTemplateTime(progress);
        const athleteTime = phaseSync.getAthleteTime(progress);
        templatePlayer.seek(templateTime);
        athletePlayer.seek(athleteTime);
      },
      [phaseSync, templatePlayer, athletePlayer]
    );

    // Handle phase selection from timeline
    const handlePhaseSelect = useCallback(
      (index: number) => {
        phaseSync.goToPhase(index);
        // Seek both videos to start of selected phase
        const templateTime = phaseSync.getTemplateTime(0, index);
        const athleteTime = phaseSync.getAthleteTime(0, index);
        templatePlayer.seek(templateTime);
        athletePlayer.seek(athleteTime);
      },
      [phaseSync, templatePlayer, athletePlayer]
    );

    // Get current frame score
    const currentFrameScore = useMemo(() => {
      const frameScore = frameScores.find(
        (fs) => fs.frame_index === athletePlayer.currentFrame
      );
      return frameScore?.score ?? null;
    }, [frameScores, athletePlayer.currentFrame]);

    // Pace badge (P2, docs/specs/playback-modes.md criterion 3) — shown on the
    // athlete panel only while retimed ("Match coach's timing") and only when
    // the CURRENT phase's timing ratio is far enough from 1 to matter (outside
    // 0.9-1.1, the same band getTimingIndicator already uses). Ratio = athlete
    // duration ÷ coach duration — the playback factor the #578 drive applies —
    // so ratio < 1 means the athlete is moving faster than the coach here.
    const paceBadgeText = useMemo((): string | null => {
      if (syncMode !== 'phase' || !hasPhaseData) return null;
      const ratio = phaseSync.getTimingRatioForPhase(phaseSync.currentPhaseIndex);
      if (ratio >= 0.9 && ratio <= 1.1) return null;
      const direction = ratio < 1 ? 'faster than the coach here' : 'slower than the coach here';
      return `${ratio.toFixed(1)}× · ${direction}`;
    }, [syncMode, hasPhaseData, phaseSync]);

    // Video dimensions and ready states
    const [athleteVideoDimensions, setAthleteVideoDimensions] = useState({ width: 0, height: 0 });
    const [templateVideoDimensions, setTemplateVideoDimensions] = useState({ width: 0, height: 0 });
    const [isAthleteVideoReady, setIsAthleteVideoReady] = useState(false);
    const [isTemplateVideoReady, setIsTemplateVideoReady] = useState(false);

    // Stable refs to the last known valid dimensions — used to restore overlay
    // state after a 3D→2D switch where the h-0 collapse briefly reports 0-height.
    const lastValidAthleteDims = useRef({ width: 0, height: 0 });
    const lastValidTemplateDims = useRef({ width: 0, height: 0 });

    // When switching back to 2D mode, the h-0 → normal-height transition may leave
    // the dimension state stale. The ResizeObserver fires with 0-height while the
    // container is collapsed and the guard suppresses the update. On returning to 2D,
    // the observer may not fire before React re-renders the overlays, causing a blank
    // frame. Force a restore using the last valid dimensions so overlays appear instantly.
    // PLACEMENT: This effect is intentionally declared here — AFTER the useState and
    // useRef declarations it references — so all variables are in-scope at hook
    // registration time and there is no forward-reference ambiguity.
    useEffect(() => {
      if (overlayState.viewMode === '2d') {
        if (lastValidAthleteDims.current.width > 0 && lastValidAthleteDims.current.height > 0) {
          setAthleteVideoDimensions({ ...lastValidAthleteDims.current });
        }
        if (lastValidTemplateDims.current.width > 0 && lastValidTemplateDims.current.height > 0) {
          setTemplateVideoDimensions({ ...lastValidTemplateDims.current });
        }
      }
    }, [overlayState.viewMode]);

    // Track video container dimensions and metadata.
    //
    // IMPORTANT: This effect intentionally includes athleteVideoUrl and templateVideoUrl
    // in its dependency array. The athlete <video> element is conditionally rendered
    // only when athleteVideoUrl is truthy (see JSX below). If the effect ran only on
    // mount, athletePlayer.videoRef.current would be null (video not yet rendered),
    // no loadedmetadata listener would be attached, and isAthleteVideoReady would
    // never become true — permanently hiding all athlete overlays. Including the URL
    // props as deps ensures the effect re-runs once they become available, at which
    // point the video element exists in the DOM and the ref is valid.
    //
    // Similarly, templateVideoUrl starts as '' (empty string) when the evaluation is
    // still loading. The ResizeObserver and loadedmetadata listener must be (re-)
    // attached once the real URL arrives and the browser fires loadedmetadata in
    // response to the explicit .load() call (see the templateVideoUrl effect above).
    useEffect(() => {
      const athleteContainer = athletePlayer.videoRef.current?.parentElement;
      const templateContainer = templatePlayer.videoRef.current?.parentElement;

      const updateDimensions = () => {
        if (athleteContainer) {
          const { width, height } = athleteContainer.getBoundingClientRect();
          if (width > 0 && height > 0) {
            lastValidAthleteDims.current = { width, height };
            setAthleteVideoDimensions({ width, height });
          }
        }
        if (templateContainer) {
          const { width, height } = templateContainer.getBoundingClientRect();
          if (width > 0 && height > 0) {
            lastValidTemplateDims.current = { width, height };
            setTemplateVideoDimensions({ width, height });
          }
        }
      };

      const observer = new ResizeObserver(updateDimensions);
      if (athleteContainer) observer.observe(athleteContainer);
      if (templateContainer) observer.observe(templateContainer);

      // Trigger an immediate dimension read — the ResizeObserver callback fires
      // asynchronously on first observe(), so without this call the dimensions
      // stay {0,0} until the next browser layout pass. Calling synchronously here
      // ensures overlays have valid pixel dimensions from the first render cycle
      // in which the video element exists.
      updateDimensions();

      // Set video ready states
      const athleteVideo = athletePlayer.videoRef.current;
      const templateVideo = templatePlayer.videoRef.current;

      const handleAthleteLoaded = () => setIsAthleteVideoReady(true);
      const handleTemplateLoaded = () => setIsTemplateVideoReady(true);

      if (athleteVideo) {
        if (athleteVideo.readyState >= 1) setIsAthleteVideoReady(true);
        athleteVideo.addEventListener('loadedmetadata', handleAthleteLoaded);
      }

      if (templateVideo) {
        if (templateVideo.readyState >= 1) setIsTemplateVideoReady(true);
        templateVideo.addEventListener('loadedmetadata', handleTemplateLoaded);
      }

      return () => {
        observer.disconnect();
        athleteVideo?.removeEventListener('loadedmetadata', handleAthleteLoaded);
        templateVideo?.removeEventListener('loadedmetadata', handleTemplateLoaded);
      };
    }, [athletePlayer.videoRef, templatePlayer.videoRef, athleteVideoUrl, templateVideoUrl]);

    const playbackSpeedOptions = [
      { value: '0.25', label: '0.25x' },
      { value: '0.5', label: '0.5x' },
      { value: '0.75', label: '0.75x' },
      { value: '1', label: '1x' },
      { value: '1.25', label: '1.25x' },
      { value: '1.5', label: '1.5x' },
      { value: '2', label: '2x' },
    ];

    // Warn when spatial goal results exist but effective_landmark fields are missing.
    // These warnings fire in both dev and production so that missing-overlay bugs are
    // visible in DevTools even on deployed builds where DEV-only guards are stripped.
    useEffect(() => {
      if (!spatialGoalResults) return;
      Object.entries(spatialGoalResults).forEach(([key, result]) => {
        if (!result) return;
        if (key.endsWith('_axis')) {
          const r = result as AxisGoalResult;
          if (!r.effective_landmark_start || !r.effective_landmark_end) {
            console.warn(
              `[EnhancedEvaluationDisplay] Axis goal "${key}" is missing effective_landmark_start/end — overlay will not render. Check backend response.`,
              { effective_landmark_start: r.effective_landmark_start, effective_landmark_end: r.effective_landmark_end }
            );
          }
        }
        if (key.endsWith('_timing')) {
          const r = result as TimingGoalResult;
          if (!r.effective_landmark_a || !r.effective_landmark_b) {
            console.warn(
              `[EnhancedEvaluationDisplay] Timing goal "${key}" is missing effective_landmark_a/b — overlay will not render. Check backend response.`,
              { effective_landmark_a: r.effective_landmark_a, effective_landmark_b: r.effective_landmark_b }
            );
          }
        }
      });
      // Also warn if pose data is missing but goal overlays need it
      const hasAxisOrTiming = Object.keys(spatialGoalResults).some(
        k => k.endsWith('_axis') || k.endsWith('_timing')
      );
      if (hasAxisOrTiming && !athletePoseData?.length) {
        console.warn(
          '[EnhancedEvaluationDisplay] Axis/timing goal overlays require athletePoseData but it is unavailable. Overlays will not render — pose cache may be missing for this video.'
        );
      }
    }, [spatialGoalResults, athletePoseData]);

    // Pose data failed to arrive (fetch settled, no frames) while overlays need
    // it — surface a VISIBLE reload affordance instead of silently-blank
    // canvases. Prod incident 2026-08-20 (self-eval 9440d89c): a transient
    // Cloudflare QUIC error dropped both pose payloads; every overlay went
    // blank with only a console warning, reading as "traces don't exist".
    const poseDataFailed =
      isPoseDataLoading === false &&
      !athletePoseData?.length &&
      !templatePoseData?.length;

    return (
      <Card className={`glass-panel flex flex-col rounded-lg overflow-hidden ${className || ''}`}>
        {poseDataFailed && (
          <div
            role="alert"
            data-testid="pose-data-failed-banner"
            className="mx-4 mt-4 rounded-sm-control border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-text-primary"
          >
            Overlay data (pose frames) failed to load, so skeletons, traces and
            region overlays can't be drawn. This is usually a temporary network
            hiccup —{' '}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline font-medium hover:text-accent"
            >
              reload the page
            </button>{' '}
            to retry.
          </div>
        )}
        {/* 2D / 3D mode switch — PERSISTENT and ABOVE the grid (sibling of both the
            grid and the 3D viewer), so it stays visible in 3D mode and offers a way
            back to 2D. Report-themed segmented control. Only shown when 3D is
            actually available (has3DData + reliable depth). Regression fix 2026-07-27
            — was a pill inside the grid, which collapses in 3D mode. */}
        {threeDAvailable && (
          <EvalViewModeSwitch
            viewMode={overlayState.viewMode}
            onChange={(mode) => setOverlayState(s => (s.viewMode === mode ? s : { ...s, viewMode: mode }))}
          />
        )}
        {/* Side-view note: rendered when 3D data exists but depth is unreliable.
            Explains why the 3D toggle is absent without cluttering the header. */}
        {ENABLE_3D_VISUALIZATION && has3DData && pose3dDepthReliable === false && (
          <div className="px-4 pt-3">
            <div
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-warning/10 border border-warning/20 text-xs text-warning"
              data-testid="side-view-3d-note"
            >
              <ExclamationCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>3D skeleton view isn't available for side-view footage.</span>
            </div>
          </div>
        )}

        {/* Video panels — hidden in 3D mode but kept mounted (invisible h-0) to preserve
            video state, dimensions state, and ResizeObserver wiring.  Using `hidden`
            (display:none) collapses the DOM and loses layout, and ResizeObserver does
            not fire reliably on display:none → display:grid transitions.
            Reference viewports (landscape phones): iPhone 14 = 844×390, Pixel 7 = 915×412.
            landscape:grid-cols-2 activates side-by-side at any landscape orientation,
            independently of the lg: (≥1024px) desktop breakpoint.
            In landscape-phone (max-lg:landscape:): the grid fills full height with no
            padding/gap; each cell is a flex column so the video div takes flex-1 space.
            Portrait/desktop (lg+ or portrait): unchanged — gap-4 p-4 grid applies. */}
        <div
          className={`grid grid-cols-1 landscape:grid-cols-2 lg:grid-cols-2 gap-4 p-4 max-lg:landscape:gap-0 max-lg:landscape:p-0 max-lg:landscape:h-full ${overlayState.viewMode === '3d' ? 'invisible h-0 overflow-hidden' : ''}`}
          data-field-id={FIELD_IDS.EVAL_VIDEO_COMPARE}
        >
          {/* Template video with body region overlay */}
          <div className="flex flex-col bg-black rounded-lg overflow-hidden max-lg:landscape:rounded-none">
            {/* In landscape-phone: video takes flex-1 of the panel column.
                In portrait/desktop: aspect-video applies as before.
                max-lg:landscape:h-[calc(100dvh-3.5rem)] gives each panel the full
                viewport height minus the shared slim control strip (3.5rem).
                The two panels are side-by-side (grid-cols-2), so each is half-width —
                this is the correct model: full HEIGHT, half WIDTH. */}
            <div
              className="relative aspect-video bg-black max-lg:landscape:flex-1 max-lg:landscape:aspect-auto max-lg:landscape:h-[calc(100dvh-4.5rem)]"
              style={{
                backgroundImage: `
                  linear-gradient(var(--results-grid-line, rgba(34,45,63,0.35)) 1px, transparent 1px),
                  linear-gradient(90deg, var(--results-grid-line, rgba(34,45,63,0.35)) 1px, transparent 1px)
                `,
                backgroundSize: '24px 24px',
              }}
            >
              {/* Corner micro-labels (prototype refinement 2026-07-28).
                  Positioned absolute inside the video container — stays above the
                  video via z-index. Only visible outside the video letterbox area
                  visually, but always rendered for accessibility. */}
              <div
                className="absolute top-0 left-0 z-10 pointer-events-none select-none"
                style={{
                  padding: '6px 10px',
                  fontFamily: 'var(--font-results-mono)',
                  fontSize: '9px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--results-text-faint)',
                }}
              >
                TEMPLATE
              </div>
              <div
                className="absolute top-0 right-0 z-10 pointer-events-none select-none"
                style={{
                  padding: '6px 10px',
                  fontFamily: 'var(--font-results-mono)',
                  fontSize: '9px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--results-text-faint)',
                }}
                data-testid="template-frame-label"
              >
                FRAME {templatePlayer.currentFrame} / {templatePlayer.totalFrames}
              </div>
              <video
                ref={templatePlayer.videoRef}
                src={templateVideoUrl}
                className={`w-full h-full object-contain ${templateVideoUnavailable ? 'invisible' : ''}`}
                playsInline
                onError={() => {
                  // Guard: an empty src ("") fires onerror immediately — that is not a
                  // genuine load failure. Only flag unavailable when the URL is non-empty
                  // (the browser actually attempted to fetch the video and failed).
                  if (templateVideoUrl) setTemplateVideoUnavailable(true);
                }}
              >
                Your browser does not support the video tag.
              </video>
              {templateVideoUnavailable && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0B0F14] rounded-lg border border-border-subtle">
                  <svg
                    className="w-12 h-12 text-text-muted opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  <p className="text-text-muted text-sm">Video no longer available</p>
                  <p className="text-text-muted/60 text-xs">Scores and coaching feedback are still accessible below</p>
                </div>
              )}
              {/* Body Region Overlay — now controlled by templateOverlayState.regions (per-panel, independent) */}
              {templateOverlayState.regions && isTemplateVideoReady && templateRegionHulls.length > 0 && templateVideoDimensions.width > 0 && templateVideoDimensions.height > 0 && (
                <BodyRegionOverlay
                  videoElement={templatePlayer.videoRef.current}
                  regionHulls={templateRegionHulls}
                  currentFrame={templatePlayer.currentFrame}
                  width={templateVideoDimensions.width}
                  height={templateVideoDimensions.height}
                  showOverlay={true}
                  overlayOpacity={opacity}
                  showHulls={true}
                  showLabels={true}
                  showScore={false}
                />
              )}
              {/* Template Pose Overlay — controlled by templateOverlayState.pose (per-panel).
                  Uses PoseOverlay (plain skeleton draw) rather than UnifiedPoseOverlay.
                  Rationale: UnifiedPoseOverlay is athlete-CENTRIC (comparison modes like
                  ghost_echo, deviation_vectors) and is NOT designed to render a template's
                  own skeleton cleanly. PoseOverlay draws a single set of landmarks with
                  solid lines — the right primitive for the reference panel.
                  Ghost pill: REMOVED from the template bar (showGhost=false) — the template
                  IS the reference; the old ghost-modulates-opacity hack read as broken
                  (owner 2026-08-01). Skeleton always renders at full opacity when pose=true.
                  MIRRORING SAFETY: templateLandmarks is the RAW extraction — the parent
                  (EvaluationResults) no longer pre-mirrors it on mirrored evals (that was
                  the bug that drew Bart's skeleton to his LEFT); the mirrored variant
                  exists separately as templateLandmarksGhost for the athlete-panel ghost. */}
              {templateOverlayState.pose && isTemplateVideoReady && templateLandmarksOwn && templateLandmarksOwn.length > 0 && templateVideoDimensions.width > 0 && templateVideoDimensions.height > 0 && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Cast is safe: PoseOverlay only reads x/y/visibility; z is never accessed at runtime. */}
                  <PoseOverlay
                    landmarks={templateLandmarksOwn as Array<{ x: number; y: number; z: number; visibility?: number }>}
                    width={templateVideoDimensions.width}
                    height={templateVideoDimensions.height}
                    landmarkColor="#4ec9b0"
                    connectionColor="#4ec9b0"
                  />
                </div>
              )}
              {/* Goal Overlay on template video — shows reference geometry (axis lines, timing
                  markers) from the template's own pose data, without athlete score values.
                  hideScores=true suppresses score badges, deviation whiskers, and pass/fail
                  badges. Now controlled by templateOverlayState.goals (per-panel, independent).
                  MIRRORING SAFETY: mirrored={false} — template is the reference frame. */}
              {isTemplateVideoReady && spatialGoalResults && Object.keys(spatialGoalResults).length > 0 && templateVideoDimensions.width > 0 && templateVideoDimensions.height > 0 && (
                <GoalOverlayLayer
                  goalResults={spatialGoalResults}
                  poseData={templatePoseData}
                  currentFrame={templatePlayer.currentFrame}
                  width={templateVideoDimensions.width}
                  height={templateVideoDimensions.height}
                  mirrored={false}
                  enabled={templateOverlayState.goals}
                  hideScores={true}
                  videoElement={templatePlayer.videoRef.current}
                />
              )}
              {/* Trajectory trail overlay on template video — multi-landmark, one RAF loop.
                  Controlled by templateOverlayState.trails (per-panel, independent). */}
              {templateTrailEnabled && templateVideoDimensions.width > 0 && templateVideoDimensions.height > 0 && (
                <TrajectoryTrailOverlay
                  width={templateVideoDimensions.width}
                  height={templateVideoDimensions.height}
                  trailConfigs={templateTrailConfigs.map(tc => ({
                    points:      tc.points,
                    maxVelocity: tc.maxVelocity,
                    palette:     tc.palette,
                  }))}
                  currentFrame={templatePlayer.currentFrame}
                  fps={effectiveTemplateFps}
                  videoElement={templatePlayer.videoRef.current}
                  fadeWindow={30}
                  enabled={templateTrailEnabled}
                  persistToEnd={templateTrailPersist}
                  style={trailStyle}
                />
              )}
            </div>
            {/* ───────────────────────────────────────────────────────────────
                TRACE POINTES — template panel hero (mirrors athlete hero).
                Always rendered (persistent, discoverable) directly under the
                template video. Uses the same MultiTrailSelector as the athlete.
                Independent state: templateTrailIndices / templateOverlayState.trails.
                data-testid="trace-pointes-hero-template" (distinct from athlete hero).
                Section header (2026-07-28 prototype): "Trace" upright + "Pointes"
                italic in Fraunces, thin rule, right-aligned mono caption.
                ─────────────────────────────────────────────────────────────── */}
            {/* Trace Pointes hero (template panel) — hidden in landscape-phone.
                A shared slim control strip replaces it in landscape (see below). */}
            <div
              data-testid="trace-pointes-hero-template"
              className="border-t max-lg:landscape:hidden"
              style={{ borderColor: 'var(--results-surface-line)', background: 'var(--results-surface-raised-1)' }}
            >
              {/* Editorial section header: "Trace Pointes" with rule + caption */}
              <div className="flex items-center gap-2 px-3 pt-2.5 pb-0.5" aria-hidden="false">
                {/* dotted-trace brand glyph */}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="3" cy="12.5" r="1.35" fill="var(--results-accent-report)" opacity="0.45" />
                  <circle cx="6" cy="9.5" r="1.5" fill="var(--results-accent-report)" opacity="0.65" />
                  <circle cx="9.5" cy="6.5" r="1.7" fill="var(--results-accent-report)" opacity="0.85" />
                  <circle cx="13" cy="3" r="1.9" fill="var(--results-accent-report)" />
                </svg>
                {/*
                 * "Trace" upright + "Pointes" italic in Fraunces.
                 * This is the Trace Pointes section header treatment (2026-07-28).
                 */}
                <span
                  style={{
                    fontFamily: 'var(--font-results-display)',
                    fontStyle: 'normal',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--results-accent-report)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Trace{' '}
                  <em style={{ fontStyle: 'italic' }}>Pointes</em>
                </span>
                {/* Thin horizontal rule — fills remaining space */}
                <span
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: '1px',
                    background: 'var(--results-section-rule)',
                    display: 'block',
                    minWidth: '12px',
                  }}
                />
                {/* Right-aligned mono caption */}
                <span
                  style={{
                    fontFamily: 'var(--font-results-mono)',
                    fontSize: '8px',
                    fontWeight: 500,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--results-text-faint)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  TEMPLATE
                </span>
              </div>
              <MultiTrailSelector
                enabled={templateTrailEnabled}
                onToggle={() => setTemplateOverlayState(s => ({ ...s, trails: !s.trails }))}
                selectedIndices={templateTrailIndices}
                onSelectionChange={(idx) => { templateTrailTouched.current = true; setTemplateTrailIndices(idx); }}
                lowVisibilityValues={[...templateMeanVis.entries()].filter(([, v]) => v < TRAIL_VIS_FLOOR).map(([k]) => k)}
                persistToEnd={templateTrailPersist}
                onPersistToggle={() => setTemplateTrailPersist(v => !v)}
                trailStyle={trailStyle}
                onTrailStyleChange={setTrailStyle}
                panelLeadSide={templateLeadSide}
              />
            </div>
            {/* Template overlay control bar — hidden in landscape-phone.
                Per-column landscape bar replaces it in landscape (see below).
                Wired to templateOverlayState. Fully independent of the athlete bar.
                Wrapped in a div with data-testid so tests can distinguish the two bars
                (the inner EvalOverlayControlBar always emits data-testid="eval-overlay-control-bar"). */}
            <div data-testid="eval-overlay-control-bar-template" className="max-lg:landscape:hidden">
              <EvalOverlayControlBar
                state={templateOverlayState}
                onPoseToggle={() => setTemplateOverlayState(s => ({ ...s, pose: !s.pose }))}
                onGhostToggle={() => setTemplateOverlayState(s => ({ ...s, ghost: !s.ghost }))}
                onGoalsToggle={() => setTemplateOverlayState(s => ({ ...s, goals: !s.goals }))}
                onRegionsToggle={() => setTemplateOverlayState(s => ({ ...s, regions: !s.regions }))}
                /* No Ghost pill on the template panel — the template IS the
                   reference; the pill previously only dimmed the skeleton,
                   which read as broken (owner 2026-08-01). */
                showGhost={false}
              />
            </div>

            {/* ── Landscape per-column compact bar — TEMPLATE (B-2026-0130) ──────────
                Always-visible in landscape-phone (hidden everywhere else).
                Height target ~4.5rem (matches calc(100dvh-4.5rem) video height).
                Contains: play/pause · Pose toggle · Trace on/off · trail chips
                via MultiTrailSelector (compact, inline row).
                Drives templateOverlayState / templateTrail* — independent of athlete.
                2D/3D toggle lives on the ATHLETE bar only (viewMode is global).
                Portrait/desktop: hidden (max-lg:landscape:flex activates only in
                landscape-phone; bare `hidden` suppresses it everywhere else). */}
            <div
              data-testid="landscape-controls-template"
              className="hidden max-lg:landscape:flex flex-col justify-center flex-shrink-0 overflow-hidden"
              style={{
                minHeight: '4.5rem',
                borderTop: '1px solid var(--results-surface-line)',
                background: 'var(--results-surface-raised-1)',
              }}
              aria-label="Template video controls"
            >
              {/* Row 1: play/pause · Pose · Trace on/off */}
              <div className="flex items-center gap-2 px-2 py-1">
                {/* Play / Pause */}
                <button
                  onClick={togglePlay}
                  disabled={!templatePlayer.isReady}
                  aria-label={templatePlayer.isPlaying ? 'Pause' : 'Play'}
                  className="flex items-center justify-center w-7 h-7 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  style={{
                    background: 'color-mix(in srgb, var(--results-accent-report) 18%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--results-accent-report) 40%, transparent)',
                    color: 'var(--results-accent-report)',
                  }}
                >
                  {templatePlayer.isPlaying ? (
                    <PauseIcon className="h-3.5 w-3.5" />
                  ) : (
                    <PlayIcon className="h-3.5 w-3.5" />
                  )}
                </button>
                {/* Divider */}
                <span
                  className="w-px h-4 flex-shrink-0"
                  style={{ background: 'var(--results-surface-line)' }}
                  aria-hidden="true"
                />
                {/* Pose on/off */}
                <button
                  onClick={() => setTemplateOverlayState(s => ({ ...s, pose: !s.pose }))}
                  aria-pressed={templateOverlayState.pose}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                  style={templateOverlayState.pose ? {
                    background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                    borderColor: 'var(--results-accent-report)',
                    color: 'var(--results-accent-report)',
                  } : {
                    background: 'var(--results-surface-raised-2)',
                    borderColor: 'var(--results-surface-line)',
                    color: 'var(--results-text-dim)',
                  }}
                >
                  Pose
                </button>
                {/* Trace on/off pill */}
                <button
                  onClick={() => setTemplateOverlayState(s => ({ ...s, trails: !s.trails }))}
                  aria-pressed={templateOverlayState.trails}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                  style={templateOverlayState.trails ? {
                    background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                    borderColor: 'var(--results-accent-report)',
                    color: 'var(--results-accent-report)',
                  } : {
                    background: 'var(--results-surface-raised-2)',
                    borderColor: 'var(--results-surface-line)',
                    color: 'var(--results-text-dim)',
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: templateOverlayState.trails ? 'var(--results-accent-report)' : 'var(--results-text-faint)' }}
                    aria-hidden="true"
                  />
                  Trace
                </button>
              </div>
              {/* Row 2: Trail landmark chips + Persist|Fade via MultiTrailSelector */}
              <MultiTrailSelector
                enabled={templateTrailEnabled}
                onToggle={() => setTemplateOverlayState(s => ({ ...s, trails: !s.trails }))}
                selectedIndices={templateTrailIndices}
                onSelectionChange={(idx) => { templateTrailTouched.current = true; setTemplateTrailIndices(idx); }}
                lowVisibilityValues={[...templateMeanVis.entries()].filter(([, v]) => v < TRAIL_VIS_FLOOR).map(([k]) => k)}
                persistToEnd={templateTrailPersist}
                onPersistToggle={() => setTemplateTrailPersist(v => !v)}
                trailStyle={trailStyle}
                onTrailStyleChange={setTrailStyle}
                panelLeadSide={templateLeadSide}
              />
            </div>
          </div>

          {/* Athlete video with body region overlay */}
          <div className="flex flex-col bg-black rounded-lg overflow-hidden max-lg:landscape:rounded-none">
            <div
              className="relative aspect-video bg-black max-lg:landscape:flex-1 max-lg:landscape:aspect-auto max-lg:landscape:h-[calc(100dvh-4.5rem)]"
              style={{
                backgroundImage: `
                  linear-gradient(var(--results-grid-line, rgba(34,45,63,0.35)) 1px, transparent 1px),
                  linear-gradient(90deg, var(--results-grid-line, rgba(34,45,63,0.35)) 1px, transparent 1px)
                `,
                backgroundSize: '24px 24px',
              }}
            >
              {/* Corner micro-labels (prototype refinement 2026-07-28).
                  top-left: ATHLETE · <name-placeholder> (no athlete name prop currently —
                  renders "ATHLETE" only; wire in a name prop when available) + the pace
                  badge (P2, docs/specs/playback-modes.md criterion 3) underneath it.
                  top-right: FRAME <cur> / <total>. */}
              <div
                className="absolute top-0 left-0 z-10 pointer-events-none select-none flex flex-col items-start gap-0.5"
                style={{ padding: '6px 10px' }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-results-mono)',
                    fontSize: '9px',
                    fontWeight: 600,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--results-text-faint)',
                  }}
                >
                  ATHLETE
                </span>
                {paceBadgeText && (
                  <span
                    data-testid="pace-badge"
                    aria-live="polite"
                    style={{
                      fontFamily: 'var(--font-results-mono)',
                      fontSize: '10px',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      padding: '2px 6px',
                      borderRadius: 'var(--results-radius-pill)',
                      background: 'rgba(0,0,0,0.45)',
                      color: 'var(--results-accent-warn, #f0b429)',
                    }}
                  >
                    {paceBadgeText}
                  </span>
                )}
              </div>
              {/* Frame counter — top-right */}
              <div
                className="absolute top-0 right-0 z-10 pointer-events-none select-none flex items-center gap-2"
                style={{
                  padding: '6px 10px',
                  fontFamily: 'var(--font-results-mono)',
                  fontSize: '9px',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--results-text-faint)',
                }}
                data-testid="athlete-frame-label"
              >
                {currentFrameScore !== null && (
                  <span
                    style={{
                      padding: '2px 6px',
                      borderRadius: 'var(--results-radius-pill)',
                      fontSize: '9px',
                      fontWeight: 700,
                      background: currentFrameScore >= 80
                        ? 'color-mix(in srgb, var(--results-accent-good) 18%, transparent)'
                        : currentFrameScore >= 60
                        ? 'color-mix(in srgb, var(--results-accent-warn) 18%, transparent)'
                        : 'color-mix(in srgb, var(--results-accent-bad) 18%, transparent)',
                      color: currentFrameScore >= 80
                        ? 'var(--results-accent-good)'
                        : currentFrameScore >= 60
                        ? 'var(--results-accent-warn)'
                        : 'var(--results-accent-bad)',
                    }}
                  >
                    {Math.round(currentFrameScore)}
                  </span>
                )}
                FRAME {athletePlayer.currentFrame} / {athletePlayer.totalFrames}
              </div>
              {athleteVideoUrl ? (
                <>
                  <video
                    ref={athletePlayer.videoRef}
                    src={athleteVideoUrl}
                    className={`w-full h-full object-contain ${athleteVideoUnavailable ? 'invisible' : ''}`}
                    playsInline
                    onError={() => setAthleteVideoUnavailable(true)}
                  >
                    Your browser does not support the video tag.
                  </video>
                  {athleteVideoUnavailable && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0B0F14] rounded-lg border border-border-subtle">
                      <svg
                        className="w-12 h-12 text-text-muted opacity-40"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                        />
                      </svg>
                      <p className="text-text-muted text-sm">Video no longer available</p>
                      <p className="text-text-muted/60 text-xs">Scores and coaching feedback are still accessible below</p>
                    </div>
                  )}
                </>
              ) : (
                /* Athlete video is ephemeral and no longer available locally.
                   The pose skeleton overlay still renders on top using pose data. */
                <div
                  ref={(el) => {
                    // Assign a dummy ref so downstream hooks don't crash on null videoRef
                    if (el && athletePlayer.videoRef && 'current' in athletePlayer.videoRef) {
                      (athletePlayer.videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = null;
                    }
                  }}
                  className="w-full h-full flex flex-col items-center justify-center gap-3 bg-[#0B0F14]"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-text-muted"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  <p className="text-sm text-text-secondary font-medium">Video not available</p>
                  <p className="text-xs text-text-muted text-center px-4">
                    Showing pose overlay — the video was processed and is no longer stored on the server.
                  </p>
                </div>
              )}
              {/* Unified Pose Overlay - controlled by overlayState.pose + ghost (via vizConfig).
                  overlayState.pose=false OR vizConfig.mode='off' → not rendered.
                  Ghost pill → setMode('ghost_echo'|'off') via the viz hook (RF#1, no new canvas). */}
              {(overlayState.pose || overlayState.ghost) && isAthleteVideoReady && athleteVideoDimensions.width > 0 && athleteVideoDimensions.height > 0 && vizConfig.mode !== 'off' && (
                <UnifiedPoseOverlay
                  videoElement={athletePlayer.videoRef.current}
                  visualizationConfig={vizConfig}
                  /* Ghost uses the MIRRORED-when-eval-mirrored variant so the
                     ghost drawn over the athlete matches their orientation.
                     (The raw templateLandmarks belong to the template panel.) */
                  templateLandmarks={templateGhostOwn}
                  athleteLandmarks={athleteLandmarksOwn}
                  landmarkDeviations={landmarkDeviations}
                  currentFrame={athletePlayer.currentFrame}
                  totalFrames={athletePlayer.totalFrames}
                  frameScores={frameScores}
                  regionHulls={athleteRegionHulls}
                  showRegions={overlayState.regions}
                  mirrored={mirrored}
                  className="absolute inset-0"
                />
              )}

              {/* Fallback Body Region Overlay — fires when the inner BodyRegionOverlay inside
                  UnifiedPoseOverlay will NOT render (viz mode 'off' / pose off / applyToRegions
                  disabled). Also gated by overlayState.regions.
                  NOTE: We intentionally do NOT pass totalFrames/hullFrameCount here.
                  Since backend hull computation uses original athlete pose data (no resampling),
                  hull frame indices match video frames exactly. Frame mapping would introduce
                  an off-by-one error due to HTML5 video duration inaccuracy (e.g., totalFrames=156
                  when actual frames=157), causing hulls to appear "ahead" of the athlete. */}
              {overlayState.regions && isAthleteVideoReady && athleteRegionHulls.length > 0 && athleteVideoDimensions.width > 0 && athleteVideoDimensions.height > 0 && (!overlayState.pose || vizConfig.mode === 'off' || !vizConfig.applyToRegions) && (
                <BodyRegionOverlay
                  videoElement={athletePlayer.videoRef.current}
                  regionHulls={athleteRegionHulls}
                  currentFrame={athletePlayer.currentFrame}
                  width={athleteVideoDimensions.width}
                  height={athleteVideoDimensions.height}
                  showOverlay={true}
                  overlayOpacity={opacity}
                  showHulls={true}
                  showLabels={false}
                  showScore={false}
                  mirrored={mirrored}
                />
              )}

              {/* Goal Overlay Layer — axis lines, timing rings, trajectory paths.
                  Driven by overlayState.goals — the independent Goals pill bit (RF#3).
                  Decoupled from showOverlays / pose / regions. */}
              {isAthleteVideoReady && spatialGoalResults && Object.keys(spatialGoalResults).length > 0 && athleteVideoDimensions.width > 0 && athleteVideoDimensions.height > 0 && (
                <GoalOverlayLayer
                  goalResults={spatialGoalResults}
                  poseData={athletePoseData}
                  currentFrame={athletePlayer.currentFrame}
                  width={athleteVideoDimensions.width}
                  height={athleteVideoDimensions.height}
                  mirrored={mirrored ?? false}
                  enabled={overlayState.goals}
                  videoElement={athletePlayer.videoRef.current}
                />
              )}
              {/* Trace Pointes — multi-landmark trail overlay on athlete video (ONE RAF loop) */}
              {overlayState.trails && athleteTrailEnabled && athleteVideoDimensions.width > 0 && athleteVideoDimensions.height > 0 && (
                <TrajectoryTrailOverlay
                  width={athleteVideoDimensions.width}
                  height={athleteVideoDimensions.height}
                  trailConfigs={athleteTrailConfigs.map(tc => ({
                    points:      tc.points,
                    maxVelocity: tc.maxVelocity,
                    palette:     tc.palette,
                  }))}
                  currentFrame={athletePlayer.currentFrame}
                  fps={effectiveAthleteFps}
                  videoElement={athletePlayer.videoRef.current}
                  fadeWindow={30}
                  enabled={athleteTrailEnabled}
                  persistToEnd={athleteTrailPersist}
                  style={trailStyle}
                />
              )}
              {/* Pose-loading placeholder — pulsing overlay shown while
                  athletePoseData / templatePoseData are still fetching.
                  Overlays (pose skeleton, goal lines) require pose data; this
                  replaces the previous "silent absence" UX. Hidden once data
                  arrives or the caller clears isPoseDataLoading. */}
              {isPoseDataLoading && !athletePoseData && (
                <div
                  className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md bg-black/70 px-2.5 py-1.5 animate-pulse"
                  aria-label="Loading pose overlay"
                  aria-live="polite"
                >
                  <svg
                    className="h-3.5 w-3.5 text-accent flex-shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                    <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round" />
                  </svg>
                  <span className="text-[11px] text-text-muted">Loading overlay...</span>
                </div>
              )}
            </div>
            {/* ───────────────────────────────────────────────────────────────
                TRACE POINTES — the signature motion-trace control (HERO).
                Always rendered (persistent, discoverable) and placed directly
                under the athlete video, above the utility overlay pills, so it
                reads as the centerpiece of the results view. On by default.
                Owns its own on/off (drives overlayState.trails); the utility
                bar no longer carries a "Trails" pill. Regression fix 2026-07-27:
                1f had demoted this to a buried, default-off bar pill.
                Section header (2026-07-28 prototype): "Trace" upright + "Pointes"
                italic in Fraunces, thin rule, right-aligned mono caption.
                ─────────────────────────────────────────────────────────────── */}
            {/* Trace Pointes hero (athlete panel) — hidden in landscape-phone.
                A shared slim control strip replaces it in landscape (see below). */}
            <div
              data-testid="trace-pointes-hero"
              className="border-t max-lg:landscape:hidden"
              style={{ borderColor: 'var(--results-surface-line)', background: 'var(--results-surface-raised-1)' }}
            >
              {/* Editorial section header: "Trace Pointes" with rule + caption */}
              <div className="flex items-center gap-2 px-3 pt-2.5 pb-0.5">
                {/* dotted-trace brand glyph */}
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="3" cy="12.5" r="1.35" fill="var(--results-accent-report)" opacity="0.45" />
                  <circle cx="6" cy="9.5" r="1.5" fill="var(--results-accent-report)" opacity="0.65" />
                  <circle cx="9.5" cy="6.5" r="1.7" fill="var(--results-accent-report)" opacity="0.85" />
                  <circle cx="13" cy="3" r="1.9" fill="var(--results-accent-report)" />
                </svg>
                {/*
                 * "Trace" upright + "Pointes" italic in Fraunces.
                 * Editorial section header (2026-07-28 prototype approval).
                 */}
                <span
                  style={{
                    fontFamily: 'var(--font-results-display)',
                    fontStyle: 'normal',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--results-accent-report)',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Trace{' '}
                  <em style={{ fontStyle: 'italic' }}>Pointes</em>
                </span>
                {/* Thin horizontal rule — fills remaining space */}
                <span
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: '1px',
                    background: 'var(--results-section-rule)',
                    display: 'block',
                    minWidth: '12px',
                  }}
                />
                {/* Right-aligned mono caption "MOTION · SPEED READS AS DEPTH" */}
                <span
                  style={{
                    fontFamily: 'var(--font-results-mono)',
                    fontSize: '8px',
                    fontWeight: 500,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--results-text-faint)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  MOTION · SPEED READS AS DEPTH
                </span>
              </div>
              <MultiTrailSelector
                enabled={athleteTrailEnabled}
                onToggle={() => setOverlayState(s => ({ ...s, trails: !s.trails }))}
                selectedIndices={athleteTrailIndices}
                onSelectionChange={(idx) => { athleteTrailTouched.current = true; setAthleteTrailIndices(idx); }}
                lowVisibilityValues={[...athleteMeanVis.entries()].filter(([, v]) => v < TRAIL_VIS_FLOOR).map(([k]) => k)}
                persistToEnd={athleteTrailPersist}
                onPersistToggle={() => setAthleteTrailPersist(v => !v)}
                trailStyle={trailStyle}
                onTrailStyleChange={setTrailStyle}
                panelLeadSide={athleteLeadSide}
              />
            </div>
            {/* ATHLETE Unified Overlay Control Bar — Slice 1f (per-panel split 2026-07-27).
                Hidden in landscape-phone — per-column landscape bar replaces it.
                Ghost pill → setMode('ghost_echo'|'off') on existing viz hook (RF#1).
                Goals/Regions/Pose pills → overlayState bits (athlete panel ONLY).
                3D Skeleton pill → overlayState.viewMode (global mode switch).
                Trace Pointes is NOT here — it has its own hero control above.
                Template panel has a SEPARATE EvalOverlayControlBar (templateOverlayState). */}
            <div className="max-lg:landscape:hidden">
              <EvalOverlayControlBar
                state={overlayState}
                onPoseToggle={togglePose}
                onGhostToggle={toggleGhost}
                onGoalsToggle={() => setOverlayState(s => ({ ...s, goals: !s.goals }))}
                onRegionsToggle={() => setOverlayState(s => ({ ...s, regions: !s.regions }))}
              />
            </div>

            {/* ── Landscape per-column compact bar — ATHLETE (B-2026-0130) ────────
                Always-visible in landscape-phone (hidden everywhere else).
                Height target ~4.5rem (matches calc(100dvh-4.5rem) video height).
                Contains: play/pause · Pose · Ghost · Trace on/off · trail chips
                via MultiTrailSelector (compact, inline row).
                Drives overlayState / athleteTrail* — independent of template.
                2D/3D toggle: included here only (viewMode is global, lives on
                overlayState). When 3D is unavailable the button is absent.
                Portrait/desktop: hidden (max-lg:landscape:flex activates only in
                landscape-phone; bare `hidden` suppresses it everywhere else). */}
            <div
              data-testid="landscape-controls-athlete"
              className="hidden max-lg:landscape:flex flex-col justify-center flex-shrink-0 overflow-hidden"
              style={{
                minHeight: '4.5rem',
                borderTop: '1px solid var(--results-surface-line)',
                background: 'var(--results-surface-raised-1)',
              }}
              aria-label="Athlete video controls"
            >
              {/* Row 1: play/pause · Pose · Ghost · Trace on/off · 2D/3D */}
              <div className="flex items-center gap-2 px-2 py-1">
                {/* Play / Pause */}
                <button
                  onClick={togglePlay}
                  disabled={!athletePlayer.isReady}
                  aria-label={athletePlayer.isPlaying ? 'Pause' : 'Play'}
                  className="flex items-center justify-center w-7 h-7 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  style={{
                    background: 'color-mix(in srgb, var(--results-accent-report) 18%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--results-accent-report) 40%, transparent)',
                    color: 'var(--results-accent-report)',
                  }}
                >
                  {athletePlayer.isPlaying ? (
                    <PauseIcon className="h-3.5 w-3.5" />
                  ) : (
                    <PlayIcon className="h-3.5 w-3.5" />
                  )}
                </button>
                {/* Divider */}
                <span
                  className="w-px h-4 flex-shrink-0"
                  style={{ background: 'var(--results-surface-line)' }}
                  aria-hidden="true"
                />
                {/* Pose on/off */}
                <button
                  onClick={togglePose}
                  aria-pressed={overlayState.pose}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                  style={overlayState.pose ? {
                    background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                    borderColor: 'var(--results-accent-report)',
                    color: 'var(--results-accent-report)',
                  } : {
                    background: 'var(--results-surface-raised-2)',
                    borderColor: 'var(--results-surface-line)',
                    color: 'var(--results-text-dim)',
                  }}
                >
                  Pose
                </button>
                {/* Ghost on/off */}
                <button
                  onClick={toggleGhost}
                  aria-pressed={overlayState.ghost}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                  style={overlayState.ghost ? {
                    background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                    borderColor: 'var(--results-accent-report)',
                    color: 'var(--results-accent-report)',
                  } : {
                    background: 'var(--results-surface-raised-2)',
                    borderColor: 'var(--results-surface-line)',
                    color: 'var(--results-text-dim)',
                  }}
                >
                  Ghost
                </button>
                {/* Trace on/off pill */}
                <button
                  onClick={() => setOverlayState(s => ({ ...s, trails: !s.trails }))}
                  aria-pressed={overlayState.trails}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors"
                  style={overlayState.trails ? {
                    background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                    borderColor: 'var(--results-accent-report)',
                    color: 'var(--results-accent-report)',
                  } : {
                    background: 'var(--results-surface-raised-2)',
                    borderColor: 'var(--results-surface-line)',
                    color: 'var(--results-text-dim)',
                  }}
                >
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: overlayState.trails ? 'var(--results-accent-report)' : 'var(--results-text-faint)' }}
                    aria-hidden="true"
                  />
                  Trace
                </button>
                {/* Spacer */}
                <span className="flex-1" aria-hidden="true" />
                {/* 2D/3D toggle — global switch, only shown when 3D is available.
                    Placed in the athlete bar (primary performance view). */}
                {threeDAvailable && (
                  <button
                    onClick={() => setOverlayState(s => ({ ...s, viewMode: s.viewMode === '3d' ? '2d' : '3d' }))}
                    aria-pressed={overlayState.viewMode === '3d'}
                    className="px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors flex-shrink-0"
                    style={overlayState.viewMode === '3d' ? {
                      background: 'color-mix(in srgb, var(--results-accent-report) 15%, transparent)',
                      borderColor: 'var(--results-accent-report)',
                      color: 'var(--results-accent-report)',
                    } : {
                      background: 'var(--results-surface-raised-2)',
                      borderColor: 'var(--results-surface-line)',
                      color: 'var(--results-text-dim)',
                    }}
                  >
                    {overlayState.viewMode === '3d' ? '3D' : '2D'}
                  </button>
                )}
              </div>
              {/* Row 2: Trail landmark chips + Persist|Fade via MultiTrailSelector */}
              <MultiTrailSelector
                enabled={athleteTrailEnabled}
                onToggle={() => setOverlayState(s => ({ ...s, trails: !s.trails }))}
                selectedIndices={athleteTrailIndices}
                onSelectionChange={(idx) => { athleteTrailTouched.current = true; setAthleteTrailIndices(idx); }}
                lowVisibilityValues={[...athleteMeanVis.entries()].filter(([, v]) => v < TRAIL_VIS_FLOOR).map(([k]) => k)}
                persistToEnd={athleteTrailPersist}
                onPersistToggle={() => setAthleteTrailPersist(v => !v)}
                trailStyle={trailStyle}
                onTrailStyleChange={setTrailStyle}
                panelLeadSide={athleteLeadSide}
              />
            </div>
          </div>
        </div>

        {/* ── (B-2026-0130) Shared slim strip and tap-to-expand sheet REMOVED ──
            Replaced by per-column compact bars (landscape-controls-athlete and
            landscape-controls-template) directly inside each video column above.
            The shared strip (landscape-slim-controls) and sheet
            (landscape-controls-sheet) are no longer rendered.
            For regression test history, see EvaluationResults.landscapeFocus.test.tsx. */}

        {/* (B-2026-0130) Shared slim strip and tap-to-expand sheet removed.
            Per-column compact bars now live inside each video column directly.
            No DOM placeholders needed — the per-column bars handle all landscape
            playback + overlay + trail controls at always-visible positions. */}

        {/* 3D Skeleton Viewer — pre-mounted as soon as 3D data is available.
            While in 2D mode the viewer is hidden with overflow:hidden + h-0 so it
            takes no layout space, but the Canvas still receives the container WIDTH
            (via the parent's width), allowing WebGL, HDR environment, and skeleton
            geometry to fully initialize in the background.  When user clicks
            "3D Skeleton", we just remove the height constraint — instant display. */}
        {ENABLE_3D_VISUALIZATION && !has3DData && (
          <div className="m-4 rounded-lg border border-border-subtle bg-dark-base overflow-hidden">
            <div className="flex flex-col items-center justify-center py-10 px-6 gap-4 text-center">
              <div className="relative w-12 h-12 opacity-35" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" className="w-full h-full">
                  <path d="M14 10 L34 10 L34 30 L14 30 Z" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                  <path d="M8 18 L28 18 L28 38 L8 38 Z" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                  <line x1="8" y1="18" x2="14" y2="10" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                  <line x1="28" y1="18" x2="34" y2="10" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                  <line x1="28" y1="38" x2="34" y2="30" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                  <line x1="8" y1="38" x2="14" y2="30" stroke="currentColor" strokeWidth="1.25" className="text-text-muted" />
                </svg>
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-text-secondary tracking-tight">3D visualization not available</p>
                <p className="text-xs text-text-muted max-w-xs leading-relaxed">
                  3D data must be cached at analysis time. Re-run the evaluation to generate it.
                </p>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.07] text-[10px] font-medium text-text-muted tracking-wide uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-white/20" aria-hidden="true" />
                No 3D cache
              </span>
            </div>
          </div>
        )}

        {/* Defer-mount the 3D viewer: only mount when overlayState.viewMode === '3d'.
            Pre-mounting inside an h-0 wrapper produced a 0×0 WebGL context that
            was killed by the GPU driver on resize storms (see fix Q2.2).
            The outer m-4 wrapper holds the layout box steady across the
            Suspense fallback → SkeletonViewer3D transition so R3F's resize
            observer never captures intermediate dimensions on first mount.
            threeDAvailable already excludes side-view (pose3dDepthReliable=false),
            so this block never renders the broken crossed-legs skeleton.
            The 3D toggle pill is in EvalOverlayControlBar (show3DToggle={threeDAvailable}). */}
        {threeDAvailable && overlayState.viewMode === '3d' && (
          <div
            className="m-4 rounded-[var(--results-radius-card)] overflow-hidden border"
            style={{ borderColor: 'var(--results-surface-line)', background: 'var(--results-surface-raised-1)' }}
          >
            {/* Report-themed header so the 3D view matches the redesigned page chrome. */}
            <div
              className="flex items-center gap-2 px-4 py-2 border-b"
              style={{ borderColor: 'var(--results-surface-line)' }}
            >
              <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true" style={{ color: 'var(--results-accent-report)' }}>
                <path d="M6 1L10.5 3.5V8.5L6 11L1.5 8.5V3.5L6 1Z M6 1V6 M6 6L10.5 3.5 M6 6L1.5 3.5 M6 6V11" stroke="currentColor" strokeWidth="0.9" strokeLinejoin="round" />
              </svg>
              <span
                className="text-[12px] font-semibold uppercase tracking-[0.12em]"
                style={{ color: 'var(--results-accent-report)', fontFamily: 'var(--font-results-display)' }}
              >
                3D Skeleton
              </span>
              <span className="text-[10px] text-text-muted/70 select-none">template vs athlete · drag to orbit</span>
            </div>
            {/* ErrorBoundary catches runtime crashes inside SkeletonViewer3D (e.g. WebGL
                init failures, R3F render errors) that would otherwise silently keep the
                Suspense fallback visible because the error propagates past Suspense. */}
            <ErrorBoundary
              fallback={
                <div className="aspect-video flex items-center justify-center" style={{ background: 'var(--results-surface-raised-2)' }}>
                  <div className="text-center space-y-2">
                    <p className="text-sm text-danger font-medium">3D viewer crashed</p>
                    <p className="text-xs text-text-muted">Open DevTools console for details</p>
                  </div>
                </div>
              }
            >
            <React.Suspense
              fallback={
                <div className="aspect-video flex items-center justify-center" style={{ background: 'var(--results-surface-raised-2)' }}>
                  <div className="text-text-muted text-sm">Loading 3D viewer…</div>
                </div>
              }
            >
              <SkeletonViewer3D
                templateJoints3D={templateJoints3D ?? null}
                athleteJoints3D={athleteJoints3D ?? null}
                boneScores={boneScores}
                isGravityAligned={isGravityAligned}
              />
            </React.Suspense>
            </ErrorBoundary>
          </div>
        )}

        {/* Phase Sync Timeline (shown when phase data is available and in phase mode).
            Hidden in landscape on small screens (phones) — the video grid needs the
            full viewport height. Visible again at lg: (desktop/tablet). */}
        {hasPhaseData && syncMode === 'phase' && (
          <div className="max-lg:landscape:hidden p-4 border-t border-border-subtle" data-field-id={FIELD_IDS.EVAL_PHASE_TAB}>
            <PhaseSyncTimeline
              phaseMapping={phaseMapping}
              currentPhaseIndex={phaseSync.currentPhaseIndex}
              phaseProgress={phaseSync.phaseProgress}
              templateFps={effectiveTemplateFps}
              athleteFps={effectiveAthleteFps}
              onPhaseSelect={handlePhaseSelect}
              onProgressChange={handlePhaseProgressChange}
            />
          </div>
        )}

        {/* Controls — hidden in landscape on small screens so videos get full height.
            Visible again at lg: breakpoint (desktop/tablet). */}
        <div className="max-lg:landscape:hidden bg-dark-base p-4 space-y-3" data-field-id={FIELD_IDS.EVAL_SCORE_BREAKDOWN}>
          {/* Playback alignment toggle (only show when phase data is available).
              Plain labels only — "Natural speed" / "Match coach's timing" — no
              Frame Sync / Phase Sync / DTW words a coach could see (spec
              docs/specs/playback-modes.md, criterion 1). */}
          {hasPhaseData && (
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-text-muted text-sm">Sync Mode:</span>
              <div
                className="flex rounded-md overflow-hidden"
                role="group"
                aria-label="Playback timing"
              >
                <button
                  type="button"
                  onClick={() => setSyncMode('frame')}
                  aria-pressed={syncMode === 'frame'}
                  aria-label="Natural speed"
                  title="Both videos at their real pace; tempo differences stay visible"
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    syncMode === 'frame'
                      ? 'bg-blue-600 text-white'
                      : 'bg-dark-elevated-2 text-text-secondary hover:bg-dark-elevated-1'
                  }`}
                >
                  Natural speed
                </button>
                <button
                  type="button"
                  onClick={() => setSyncMode('phase')}
                  aria-pressed={syncMode === 'phase'}
                  aria-label="Match coach's timing"
                  title="Plays the athlete at the coach's pace in each phase so matching moments line up"
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    syncMode === 'phase'
                      ? 'bg-blue-600 text-white'
                      : 'bg-dark-elevated-2 text-text-secondary hover:bg-dark-elevated-1'
                  }`}
                >
                  Match coach's timing
                </button>
              </div>
            </div>
          )}

          {/* Per-phase durations (P2, docs/specs/playback-modes.md criterion 4) — shown in
              BOTH modes whenever phase data exists, so a coach always sees the tempo
              difference even in Natural speed. "you" = the athlete panel's owner,
              "coach" = the template. Seconds are each phase's own frame count divided by
              its own panel's fps (never the ratio — no "DTW", no internal vocabulary). */}
          {hasPhaseData && (
            <div
              data-testid="phase-durations"
              className="flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 text-[11px] text-text-muted"
            >
              {(phaseMapping?.phase_pairs ?? []).map((pair, i) => {
                const athleteSeconds = (pair.athlete_phase.duration_frames / effectiveAthleteFps).toFixed(1);
                const coachSeconds = (pair.template_phase.duration_frames / effectiveTemplateFps).toFixed(1);
                const label = pair.template_phase.label || pair.athlete_phase.label || `Phase ${i + 1}`;
                return (
                  <span key={pair.template_phase.phase_id || `phase-${i}`}>
                    {label} · you {athleteSeconds} s · coach {coachSeconds} s
                  </span>
                );
              })}
            </div>
          )}

          {/* Timeline (frame-based) - shown when not in phase mode or as secondary */}
          <div className="space-y-1">
            <input
              type="range"
              min={0}
              max={templatePlayer.duration}
              step={0.01}
              value={templatePlayer.currentTime}
              onChange={(e) => handleSeek(parseFloat(e.target.value))}
              disabled={!templatePlayer.isReady}
              className="w-full h-2 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer accent-primary-600"
              aria-label="Video timeline"
            />
            <div className="flex justify-between text-xs text-text-muted">
              <span>{formatDuration(templatePlayer.currentTime)}</span>
              <span>
                Frame {templatePlayer.currentFrame} / {templatePlayer.totalFrames}
              </span>
              <span>{formatDuration(templatePlayer.duration)}</span>
            </div>
          </div>

          {/* Playback controls */}
          <div className="flex items-center justify-between">
            {/* Left: Frame/Phase navigation */}
            <div className="flex items-center gap-2">
              {/* Phase navigation buttons (when in phase mode) */}
              {syncMode === 'phase' && hasPhaseData && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={phaseSync.prevPhase}
                    disabled={phaseSync.currentPhaseIndex === 0}
                    className="text-text-primary hover:bg-dark-elevated-1"
                    aria-label="Previous phase"
                    title="Previous Phase"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                    <ChevronLeftIcon className="h-5 w-5 -ml-3" />
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={previousFrame}
                disabled={!templatePlayer.isReady || templatePlayer.currentFrame === 0}
                className="text-text-primary hover:bg-dark-elevated-1"
                aria-label="Previous frame"
              >
                <BackwardIcon className="h-5 w-5" />
              </Button>

              <Button
                variant="primary"
                size="md"
                onClick={togglePlay}
                disabled={!templatePlayer.isReady}
                aria-label={templatePlayer.isPlaying ? 'Pause' : 'Play'}
              >
                {templatePlayer.isPlaying ? (
                  <PauseIcon className="h-6 w-6" />
                ) : (
                  <PlayIcon className="h-6 w-6" />
                )}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={nextFrame}
                disabled={
                  !templatePlayer.isReady ||
                  templatePlayer.currentFrame >= templatePlayer.totalFrames
                }
                className="text-text-primary hover:bg-dark-elevated-1"
                aria-label="Next frame"
              >
                <ForwardIcon className="h-5 w-5" />
              </Button>

              {/* Phase navigation buttons (when in phase mode) */}
              {syncMode === 'phase' && hasPhaseData && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={phaseSync.nextPhase}
                    disabled={phaseSync.currentPhaseIndex >= phaseSync.totalPhases - 1}
                    className="text-text-primary hover:bg-dark-elevated-1"
                    aria-label="Next phase"
                    title="Next Phase"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                    <ChevronRightIcon className="h-5 w-5 -ml-3" />
                  </Button>
                </>
              )}
            </div>

            {/* Center: Speed control */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* The overlay controls (pose/ghost/trails/goals/regions/3D) are now in the
                  EvalOverlayControlBar on the athlete panel. Only playback speed remains here. */}
              <Select
                value={playbackSpeed.toString()}
                onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                options={playbackSpeedOptions}
                className="bg-dark-elevated-1 text-text-primary text-sm border-border-subtle"
              />
            </div>
          </div>
        </div>
      </Card>
    );
  }
);

EnhancedEvaluationDisplay.displayName = 'EnhancedEvaluationDisplay';

export default EnhancedEvaluationDisplay;
